import { Collection } from './collections.utility';
import { deepMerge } from './deep-merge.utility';
import { EMPTY_USER_ABILITIES } from './empty-user-abilities.const';
import { PackUnitType } from '../enums/pack-unit-type.enum';
import { InjectionMoldingSubModule, MetalsSubModule, PrismModule } from '../enums/prism-module.enum';
import { ObsessedEngineeringComponentType } from '../models/obsessed-engineering-component.model';

export const APPWRITE_COLLECTION_FIELDS: Partial<Record<Collection, string[]>> = {
    [Collection.email]: ['recipients', 'ccs', 'bccs', 'subject', 'body', 'bodyAsHtml', 'attachments', 'fromEmail', 'fromName', 'replyToEmail', 'replyToName', 'status', 'createdOn', 'createdById'],
    [Collection.billOfMaterialsLineItem]: ['entityId', 'quantity', 'productVersionId', 'type'],
    [Collection.clientDevice]: ['name', 'description', 'type', 'ipAddress', 'defaultUrl', 'assetId', 'defaultLoginUserId', 'lastCommunicationTimestamp', 'versionTimestamp', 'loggedInUserId', 'softwareVersionCode', 'monitored', 'googleCloudAdminDeviceId'],
    [Collection.clientDeviceLog]: ['clientDeviceId', 'message', 'location', 'timestamp'],
    [Collection.complaintLogProduct]: ['complaintLogId', 'productId', 'quantity', 'notes'],
    [Collection.complaintLog]: ['customerId', 'title', 'notes', 'status', 'createdOn', 'createdById', 'uploadedFiles', 'comments'],
    [Collection.plasticsRework]: ['customerId', 'complaintLogId', 'productId', 'quantity', 'status', 'returnedOn', 'reworkedOn', 'notes', 'reworkProcedureId', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.plasticsHardwareItemShipment]: ['createdOn', 'createdById', 'updatedOn', 'updatedById', 'customerId', 'internalNotes', 'externalNotes', 'receivedOn', 'status', 'uploadedFiles'],
    [Collection.plasticsHardwareItemShipmentItem]: ['plasticsHardwareItemShipmentId', 'hardwareItemId', 'quantity', 'createdOn'],
    [Collection.plasticsHardwareItemShipmentDamageItem]: ['plasticsHardwareItemShipmentId', 'hardwareItemId', 'damageQuantity', 'damageDescription', 'uploadedFiles', 'createdOn'],
    [Collection.obsessedEngineeringComponentShipment]: ['createdOn', 'createdById', 'updatedOn', 'updatedById', 'notes', 'receivedOn', 'status', 'uploadedFiles', 'reportEmailIds', 'reportEmailId'],
    [Collection.obsessedEngineeringComponentShipmentItem]: ['oeComponentShipmentId', 'componentId', 'quantity', 'createdOn'],
    [Collection.obsessedEngineeringComponentShipmentDamageItem]: ['oeComponentShipmentId', 'componentId', 'damageQuantity', 'damageDescription', 'uploadedFiles', 'createdOn'],
    [Collection.contact]: ['name', 'imageUrl', 'thumbnailImageUrl', 'title', 'notes', 'primaryEmail', 'emails', 'associatedCustomerIds', 'associatedVendorIds', 'phoneNumbers', 'primaryPhoneNumber', 'isOneTimeUse', 'createdById', 'createdOn'],
    [Collection.customer]: ['name', 'terms', 'imageUrl', 'thumbnailImageUrl', 'partNumberPrefix', 'plasticsPartNumberPrefix', 'metalsPartNumberPrefix', 'c3PartNumberPrefix', 'injectionMoldingPartNumberPrefix', 'isMetalsCustomer', 'isPlasticsCustomer', 'isC3Customer', 'isInjectionMoldingCustomer', 'lastSavedOn', 'lastSavedById', 'labelImageType', 'upsAccounts', 'files', 'notesCurrentVersionId'],
    [Collection.customerNoteVersion]: ['customerId', 'plain', 'createdOn', 'createdById'],
    // Address fields are FLAT columns (queryable by region/city/postal); the conversion
    // handlers flatten `address` on write and rebuild it on read.
    [Collection.customerAddress]: ['customerId', 'label', 'line1', 'line2', 'city', 'region', 'postalCode', 'country', 'isShippingAddress', 'isBillingAddress', 'isDefault', 'isOneTimeUse', 'receiverName', 'contactId', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.dockInspectionInstance]: ['createdOn', 'createdById', 'productVersionId', 'skidId', 'qualityAlertId', 'updatedOn', 'updatedById', 'status', 'inspectionInstanceHistory'],
    [Collection.globalVariables]: ['kind', 'valueJson', 'schemaVersion', 'updatedOn'],
    [Collection.inventoryTransaction]: ['entityId', 'change', 'comment', 'createdOn', 'createdById', 'type', 'processId', 'transactionGroupId'],
    [Collection.labelPrinter]: ["name", "description", "ipAddress", "lastCommunicationTimestamp"],
    [Collection.labelPrinterGroup]: ["name", "labelPrinterIds", "contexts"],
    [Collection.wifiOutlet]: ['name', 'description', 'category', 'driver', 'ipAddress', 'switchId', 'enabled', 'maxOnDurationSeconds'],
    [Collection.factoryAsset]: ["name", "description", "imageUrl", "thumbnailImageUrl", "tagIds", "createdById", "createdOn", "type"],
    [Collection.resourceSearchPackage]: ['resourceId', 'version', 'sourceSha256', 'indexFileId', 'processedFileId', 'manifestFileId', 'documentCount', 'status', 'updatedOn', 'updatedBy'],
    [Collection.assetHistoryEvent]: ["title", "description", "createdOn", "createdById", "assetId"],
    [Collection.mappingAssetToResource]: ["assetId", "resourceId", "type", "name"],
    [Collection.mappingAssetToProcedure]: ["assetId", "procedureId"],
    [Collection.factoryNode]: ["deployedFactoryNodeConfigId", "lastCommunication", "startUpTime", "monitored"],
    [Collection.factoryNodeConfig]: ["factoryNodeId", "displayName", "machineName", "description", "ipAddress", "configNote", "logicBody", "devices", "virtualChannels", "screenConfigs", "constantConfigs", "interfaceConfigs", "enumConfigs", "released", "lastSaved", "versionNumber"],
    [Collection.robotProgramGroup]: ["name", "description", "updatedById", "updatedOn", "createdById", "createdOn"],
    [Collection.robotProgram]: ["name", "programGroupId", "steps", "status", "versionNumber", "releaseNote", "releasedOn", "releasedById", "updatedOn", "updatedById", "downloadUrl", "stlDownloadUrl", "sceneSetup"],
    [Collection.plasticsMold]: ["name", "moldFamilyId", "createdOn", "createdById", "updatedOn", "updatedById", "customerId", "serialNumber", "status", "totalCycleCount", "files", 'checkupInterval', 'lastCheckupCycleCount', 'checkupOnNextRemoval', 'placementJson', 'openCheckupId'],
    [Collection.plasticsMoldHistoryEvent]: ['moldId', 'title', 'description', 'occurredOn', 'files', 'changeLog', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.moldArmCycle]: ['moldId', 'moldingTaskId', 'completedOn', 'productVersionId'],
    [Collection.mappingPlasticsMaterialToQualityAlert]: ["materialId", "qualityAlertId"],
    [Collection.plasticsQualityAlertInspection]: ["createdOn", "createdById", "productVersionId", "qualityAlertId", "imageDownloadUrls", "entityId", "taskId", "inspectionType", "status"],
    [Collection.qualityAlertViewTally]: ["rotoMachineId", "finishingStationId", "qualityAlertId", "timestamp"],
    [Collection.purchaseRequestList]: ["createdOn", "followerIds", "name"],
    [Collection.purchaseRequest]: ["budgetExempt", "canceledById", "canceledOn", "changeLog", "comments", "completedOn", "createdById", "createdOn", "description", "files", "hardwareId", "leadTimeBusinessDays", "leadTimeSource", "needDate", "note", "noteUpdatedById", "noteUpdatedOn", "ordered", "partNumber", "plannedOrderDate", "productUrl", "purchaseRequestListId", "purchaseRequestType", "quantity", "readiedById", "readiedOn", "unitPrice", "units", "vendorHardwareDataId", "vendorId"],
    [Collection.purchaseOrder]: ["expectedArrivalDate", "orderedById", "orderedOn", "purchaseOrderNumber", "purchaseRequestId", "quantity", "receivedById", "receivedOn"],
    [Collection.purchaseRequestArrival]: ["listId", "listName", "requestData", "userIds"],
    [Collection.purchaseOrderChange]: ["changeType", "listId", "listName", "requestData", "userIds"],
    [Collection.purchaseRequestUnreadied]: ["requestData", "userId"],
    [Collection.c3HardwareItem]: ["description", "imageUrl", "lastSavedOn", "lastSavedbyId", "partNumber", "qtyOnHand", "minimumStock", "maximumStock", "stockLimitEnabled", "defaultVendorHardwareDataId", "thumbnailImageUrl", "type", "units"],
    [Collection.c3VendorHardwareData]: ["active", "cost", "description", "lastSavedById", "lastSavedOn", "packageQuantity", "parentHardwareItemId", "sku", "vendorId", "websiteUrl"],
    [Collection.vendorHardwareData]: ["active", "amountGroupCount", "cost", "description", "lastSavedById", "lastSavedOn", "packageQuantity", "parentHardwareItemId", "sku", "units", "vendorId", "websiteUrl"],
    [Collection.vendorHardwareDataPriceHistory]: ["vendorHardwareDataId", "parentHardwareItemId", "vendorId", "unitCost", "cost", "packageQuantity", "amountGroupCount", "units", "createdOn", "createdById"],
    [Collection.c3Product]: ["qtyOnHand"],
    [Collection.c3ProductVersion]: ["createdById", "createdOn", "customerId", "customerPartNumber", "description", "drawingResourceId", "imageUrl", "partNumber", "productId", "releaseComment", "releasedById", "releasedOn", "status", "thumbnailImageUrl", "type", "updatedById", "updatedOn", "versionNumber"],
    [Collection.c3ProductBillOfMaterialsLineItem]: ["entityId", "productVersionId", "quantity", "type"],
    [Collection.c3Graphic]: ["archived", "createdById", "createdOn"],
    [Collection.c3GraphicVersion]: ["graphicId", "status", "versionNumber", "code", "name", "description", "graphicType", "customerId", "modelCompatibility", "placements", "keywords", "visibleToOrders", "internalNotes", "conversionStatus", "conversionMessage", "originalFileName", "originalSha256", "epsFileId", "pdfFileId", "svgFileId", "previewPngFileId", "thumbnailPngFileId", "boundingBox", "sourceVersionId", "createdById", "createdOn", "updatedById", "updatedOn", "releasedById", "releasedOn", "releaseComment"],
    [Collection.c3GraphicResource]: ["graphicId", "graphicVersionId", "placement", "kind", "status", "fileId", "fileName", "mimeType", "sizeBytes", "sha256", "sourceResourceId", "inherited", "message", "createdById", "createdOn"],
    [Collection.printingProduct]: ['archived', 'createdById', 'createdOn'],
    [Collection.printingProductVersion]: ['productId', 'versionNumber', 'status', 'type', 'name', 'description', 'sourceVersionId', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment'],
    [Collection.printingProductFile]: ['productId', 'productVersionId', 'fileId', 'fileName', 'mimeType', 'sizeBytes', 'createdById', 'createdOn'],
    [Collection.printingProductBomItem]: ['parentVersionId', 'childProductId', 'childVersionId', 'quantity', 'sortOrder'],
    [Collection.motionmasterProgram]: ['programNumber', 'kind', 'archived', 'createdById', 'createdOn', 'homeColumn', 'homeRow', 'footprintColumns', 'footprintRows'],
    [Collection.motionmasterProgramVersion]: ['programId', 'versionNumber', 'status', 'name', 'description', 'text', 'sizeBytes', 'digest', 'controlStamp', 'source', 'sourceVersionId', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment', 'layout'],
    [Collection.motionmasterTable]: ['columns', 'rows', 'pitch', 'originX', 'originY', 'offsetCode', 'measuredOn', 'surfaceZ', 'surfaceMeasuredOn', 'updatedById', 'updatedOn'],
    [Collection.injectionMoldingProduct]: ["qtyOnHand"],
    [Collection.injectionMoldingProductVersion]: ["productId", "status", "versionNumber", "partNumber", "description", "productionNotes", "imageUrl", "thumbnailImageUrl", "customerId", "customerPartNumber", "drawingResourceId", "defaultBoxQuantity", "floorVisibilityDays", "estimatedUnitsPerDay", "createdById", "createdOn", "updatedById", "updatedOn", "releasedById", "releasedOn", "releaseComment"],
    [Collection.injectionMoldingProductBillOfMaterialsLineItem]: ["productVersionId", "entityId", "quantity", "type"],
    [Collection.injectionMoldingQualityAlert]: ["title", "markdown", "translatedTitle", "translatedMarkdown"],
    [Collection.mappingInjectionMoldingProductVersionToQualityAlert]: ["productVersionId", "qualityAlertId", "checkIntervalPercent"],
    [Collection.injectionMoldingHardwareItem]: ["partNumber", "description", "imageUrl", "thumbnailImageUrl", "qtyOnHand", "type", "status", "units", "createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.injectionMoldingMaterial]: ["identifier", "description", "imageUrl", "thumbnailImageUrl", "qtyOnHand", "status", "createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.injectionMoldingMaterialRecipeItem]: ["materialId", "hardwareItemId", "ratio", "sortOrder"],
    [Collection.injectionMoldingVendorHardwareData]: ["parentHardwareItemId", "active", "vendorId", "sku", "cost", "packageQuantity", "units", "description", "websiteUrl", "lastSavedOn", "lastSavedById"],
    [Collection.injectionMoldingOrder]: ['orderNumber', 'customerId', 'customerPurchaseOrderNumber', 'shippingAddressId', 'shippingContactId', 'files', 'createdById', 'createdOn', 'updatedById', 'updatedOn'],
    [Collection.injectionMoldingOrderedProduct]: ['injectionMoldingOrderId', 'productId', 'quantity', 'completedQuantity', 'dueDate', 'salesOrderNumber', 'status', 'note', 'lotTraceability', 'lotTraceabilityHistory', 'comments', 'completedById', 'completedOn', 'changeLog', 'createdById', 'createdOn', 'updatedById', 'updatedOn'],
    [Collection.injectionMoldingProductionEvent]: ['orderedProductId', 'injectionMoldingOrderId', 'productId', 'productVersionId', 'type', 'quantityChange', 'completedQuantityBefore', 'completedQuantityAfter', 'occurredOn', 'createdById', 'backfilled'],
    [Collection.injectionMoldingMold]: ['name', 'description', 'customerId', 'owner', 'manufacturer', 'serialNumber', 'assetTag', 'status', 'storageLocation', 'cavityCount', 'totalCycleCount', 'notes', 'specifications', 'files', 'changeLog', 'createdById', 'createdOn', 'updatedById', 'updatedOn'],
    [Collection.injectionMoldingMoldSetup]: ['moldId', 'productId', 'productVersionId', 'machineAssetId', 'status', 'cycleTimeSeconds', 'activeCavityCount', 'setupTimeMinutes', 'processNotes', 'startupNotes', 'shutdownNotes', 'qualityNotes', 'packagingNotes', 'sourceNotes', 'confidence', 'verifiedById', 'verifiedOn', 'parameters', 'files', 'changeLog', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment'],
    [Collection.c3RiderBomNode]: ["parentId", "type", "name", "partNumber", "quantity", "imageUrl", "thumbnailImageUrl", "sortOrder"],
    [Collection.c3Report]: ["type", "title", "reportDate", "sourceId", "createdOn", "createdById", "payloadJson"],
    [Collection.c3InventoryThresholdReport]: ["reportId", "belowMinimumCount", "nearMinimumCount", "healthyCount", "configuredItemCount", "unconfiguredItemCount", "notifiedOn", "notificationUserIds"],
    [Collection.c3InventoryThresholdReportItem]: ["reportId", "hardwareItemId", "partNumber", "description", "qtyOnHand", "units", "minimumStock", "maximumStock", "stockStatus", "suggestedOrderQuantity", "vendorId", "vendorName", "vendorSku", "vendorDescription"],
    [Collection.plasticsManufacturingRates]: ["createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.plasticsManufacturingRatesVersion]: ["manufacturingRatesId", "name", "status", "versionNumber", "largeMachineHourlyRate", "largeMachineDefaultMinutes", "smallMachineHourlyRate", "smallMachineDefaultMinutes", "basicTrimmingCostPerMinute", "deflashingCostPerMinute", "precisionTrimmingCostPerMinute", "flamePolishCostPerMinute", "assemblyCostPerMinute", "packagingCostPerMinute", "qualityTestingCostPerMinute", "foamFillingCostPerMinute", "otherCostPerMinute", "moldingFixedCostPerPart", "resinSurchargePerPound", "materialCategoryRates", "notes", "createdById", "createdOn", "updatedById", "updatedOn", "releasedById", "releasedOn", "releaseComment"],
    [Collection.quoteStudio]: ["name", "status", "customerId", "customerName", "imageUrl", "thumbnailUrl", "createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.quoteStudioCostModel]: ["studioId", "partNumber", "description", "imageUrl", "imageThumbnailUrl", "modelUrl", "modelFileName", "thumbnailUrl", "createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.quoteStudioCostModelVersion]: ["costModelId", "versionNumber", "status", "releasedOn", "releasedById", "variations", "quotedVariationId", "x", "y", "createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.quoteStudioLink]: ["studioId", "from", "to", "quantity"],
    [Collection.quoteStudioQuote]: ["studioId", "number", "customerId", "customerName", "contactId", "contactName", "billingAddressId", "quoteType", "status", "releasedOn", "releasedById", "x", "y", "lines", "tooling", "attachments", "terms", "disposition", "statusHistory", "showSummaryToolingTotal", "notes", "printedNotes", "source", "sourceAsOf", "createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.softwareRequest]: ["createdById", "createdOn", "description", "rejectionReason", "softwareTaskId", "status", "title", "updatedById", "updatedOn"],
    [Collection.hotListItem]: ['customerId', 'notes', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.hotListOrderItem]: ['hotListItemId', 'orderedProductId', 'quantity', 'hotDate', 'reason', 'releasedOn', 'releasedById', 'resolvedOn', 'resolvedById', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.smartJeffProject]: ['title', 'summary', 'issueMarkdown', 'status', 'followerIds', 'files', 'updates', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'projectNumber', 'completedOn', 'completedById'],
    [Collection.commonProject]: ['title', 'summaryMarkdown', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.commonProjectUpdate]: ['projectId', 'markdown', 'files', 'sourceEmail', 'sourceMessageId', 'timestamp', 'createdById'],
    [Collection.capitalizationProject]: ['name', 'amount', 'description', 'notes', 'completionDate', 'pictures', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.softwareTask]: ["assignedToId", "createdById", "createdOn", "description", "followerUserIds", "hot", "status", "title", "updatedById", "updatedOn", "uploadedFiles"],
    [Collection.knowledgeDocument]: ["createdBy", "createdOn", "markdownText", "title", "updatedBy", "updatedOn", "uploadedFiles"],
    [Collection.engineeringUpdateMeeting]: ["complete", "meetingDate"],
    [Collection.engineeringUpdateMeetingUserContent]: ["futureWork", "issues", "meetingId", "userId", "workDone"],
    [Collection.financeEntry]: ["amount", "createdById", "createdOn", "type", "updatedById", "updatedOn"],
    [Collection.overtimeSummaryWeek]: ["weekOf", "entries", "note", "createdById", "createdOn", "updatedById", "updatedOn"],
    [Collection.directChat]: ["createdOn", "files", "lastMessage", "messages", "mutedByUserIds", "participantIdKey", "participantIds", "typingUsers", "updatedOn"],
    [Collection.directChatMessage]: ["chatId", "createdOn", "senderId", "text"],
    [Collection.userTimeSession]: ["comments", "date", "hoursWorked", "status", "updateById", "updatedOn", "userId"],
    [Collection.cronJob]: ["timestamp", "type"],
    [Collection.gmailUserLogin]: ["eventType", "gmailUserId", "ipAddress", "location", "onSite", "time"],
    [Collection.gmailUser]: ["email", "firestoreDeviceIds", "fullName", "isAdmin"],
    [Collection.gmailUserDevice]: ["createdOn", "deviceId", "deviceModel", "deviceType", "lastSync", "osVersion"],
    [Collection.cloudFunctionFlag]: ["enabled"],
    [Collection.databaseBackupRecord]: ["collectionDocuments", "collectionReadTimeDict", "createdOn", "executionTime", "newFilesDownloaded", "totalDocuments"],
    [Collection.storageBackupRecord]: ["createdOn", "executionTime", "newFilesDownloaded"],
    [Collection.deviceDocumentReads]: ["clientDeviceId", "documentReadCount", "timestamp", "userId"],
    [Collection.trendsDataFileRecord]: ["factoryNodeId", "timestamp"],
    [Collection.cribbageGame]: ["date", "firstPoints", "firstUserId", "imageDownloadUrl", "secondPoints", "secondUserId", "thirdPoints", "thirdUserId"],
    [Collection.stampingStation]: ["assetId", "name", "taskIds"],
    [Collection.stampingTask]: ["completedById", "completedOn", "completedQuantity", "note", "productId", "productVersionIdWhenCompleted", "requiredQuantity", "stampingStationId", "status", "updatedById", "updatedOn"],
    [Collection.freightCarrier]: ["imageUrl", "isUpsGround", "name", "websiteUrl"],
    [Collection.truckingCarrier]: ["imageUrl", "name"],
    [Collection.schematic]: ["schematicNumber"],
    [Collection.schematicVersion]: ["schematicId", "versionNumber", "status", "name", "description", "nodes", "wires", "createdOn", "updatedOn", "releasedOn", "releasedById", "releaseComment"],
    [Collection.automationHardwareItem]: ["partNumber", "description", "imageUrl", "thumbnailImageUrl", "units", "type", "footprintId", "lastSavedOn", "lastSavedById"],
    [Collection.automationVendorHardwareData]: ["parentHardwareItemId", "active", "vendorId", "sku", "cost", "packageQuantity", "description", "websiteUrl", "lastSavedOn", "lastSavedById"],
    [Collection.automationFootprint]: [],
    [Collection.automationFootprintVersion]: ["footprintId", "versionNumber", "status", "name", "width", "height", "shapes", "terminals", "createdById", "createdOn", "updatedById", "updatedOn", "releasedById", "releasedOn", "releaseComment"],
    [Collection.engineeringProjectEngineer]: ["userId", "createdOn", "createdById"],
    [Collection.engineeringProject]: ["engineerId", "title", "successCriteria", "markdownText", "createdOn", "createdById", "updatedOn", "updatedById"],
    [Collection.engineeringFileProject]: ['name', 'latestRevisionId', 'latestRevisionNumber', 'draftRevisionId', 'releasedRevisionId', 'generation', 'createdOn', 'createdById', 'updatedOn', 'lastCommandId', 'lastCommandHash', 'lastCommandById'],
    [Collection.engineeringFileRevision]: ['projectId', 'revisionNumber', 'sourceRevisionId', 'status', 'payloadJson', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'releasedOn', 'releasedById', 'releaseComment'],
    [Collection.trendsShareLink]: ["curveColorsDict", "factoryNodeId", "plottedChannelIds", "viewMaxX", "viewMaxY", "viewMinX", "viewMinY"],
    [Collection.mappingComplaintLogToQualityAlert]: ['complaintLogId', 'qualityAlertId'],
    [Collection.mappingComplaintLogToContact]: ['complaintLogId', 'contactId'],
    [Collection.mappingProductVersionToDockInspectionQualityAlert]: ['productVersionId', 'qualityAlertId'],
    [Collection.mappingProductVersionToPlasticsStaticLabel]: ['productVersionId', 'staticLabelId'],
    [Collection.mappingProductVersionToQuoteStudioCostModel]: ['productVersionId', 'costModelId'],
    [Collection.mappingProductVersionToQuoteStudioQuote]: ['productVersionId', 'quoteId'],
    [Collection.mappingProductVersionToQualityAlert]: ['productVersionId', 'qualityAlertId'],
    [Collection.maintenanceTask]: ['title', 'notes', 'followerIds', 'assetIds', 'files', 'lockoutTagOutRequired', 'updatedOn', 'updatedById', 'createdOn', 'createdById', 'taskNumber', 'status', 'eventsHistory'],
    [Collection.factoryMoveRequest]: ['title', 'description', 'fromDepartment', 'fromLocation', 'toDepartment', 'toLocation', 'deadline', 'handlingNotes', 'files', 'linkedItems', 'linkedOrderLineIds', 'followerIds', 'status', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'completedOn', 'completedById', 'canceledOn', 'canceledById'],
    [Collection.metalsHardwareItem]: ['id', 'partNumber', 'type', 'description', 'imageUrl', 'qtyOnHand', 'thumbnailImageUrl', 'units', 'lastSavedOn', 'lastSavedById'],
    [Collection.metalsLaserMaterial]: ['id', 'name', 'type', 'thickness'],
    [Collection.metalsLaserProgram]: ['id', 'programNumber', 'materialId', 'programFilePath', 'note', 'laserAssetId', 'updatedOn', 'createdOn', 'updatedById', 'createdById', 'status', 'taskIds', 'cycleCount', 'completedCycleCount', 'taskQuantityDict', 'taskCompletedQuantityDict'],
    [Collection.metalsOrder]: ['id', 'orderNumber', 'customerId', 'customerPurchaseOrderNumber', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'files'],
    [Collection.metalsOrderedProduct]: ['id', 'metalsOrderId', 'quantity', 'customerId', 'dueDate', 'salesOrderNumber', 'note', 'productionNote', 'translatedProductionNote', 'status', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'changeLog', 'productionPlanComplete', 'type', 'productId', 'partNumber', 'description'],
    [Collection.metalsQuote]: ['id', 'quoteNumber', 'status', 'customerId', 'customerName', 'partNumber', 'description', 'preparedBy', 'notes', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'releasedOn', 'dxfFileId', 'dxfFileName', 'materialFamily', 'materialId', 'pricePerLbOverride', 'materialCostMode', 'sheetCost', 'spacingIn', 'marginIn', 'rotations', 'timeBudgetMs', 'sheetSizes', 'selectedSheetId', 'factor', 'roundUnitPrices', 'hardwareLines', 'processingLines', 'priceBreakLines', 'autoMinQty', 'autoMaxQty', 'autoBreaks', 'autoRate', 'summary', 'attachments', 'kind', 'scans', 'obsolete'],
    [Collection.obsessedEngineeringProduct]: ['qtyOnHand'],
    [Collection.obsessedEngineeringProductVersion]: ['productId', 'status', 'versionNumber', 'partNumber', 'customerPartNumber', 'description', 'imageUrl', 'thumbnailImageUrl', 'requiresSerial', 'assemblyCost', 'pickingCost', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment'],
    [Collection.obsessedEngineeringProductBomLineItem]: ['productVersionId', 'componentId', 'quantity'],
    [Collection.obsessedEngineeringOrder]: ['orderNumber', 'type', 'customerPurchaseOrderNumber', 'status', 'releasedOn', 'dueDate', 'notes', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'files', 'changeLog'],
    [Collection.obsessedEngineeringInvoice]: ['orderId', 'revisionNumber', 'status', 'releaseKey', 'snapshot', 'invoicePdfFileId', 'invoicePdfDownloadUrl', 'createdOn', 'createdById', 'releasedOn', 'releasedById'],
    [Collection.c3Order]: ['orderNumber', 'customerId', 'customerPurchaseOrderNumber', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'files'],
    [Collection.c3OrderedProduct]: ['c3OrderId', 'productId', 'quantity', 'dueDate', 'salesOrderNumber', 'status', 'graphicId', 'graphicVersionId', 'note', 'translatedNote', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'changeLog'],
    [Collection.obsessedEngineeringOrderedProduct]: ['obsessedEngineeringOrderId', 'productId', 'qty', 'metalsProjectIds', 'createdById', 'createdOn', 'updatedById', 'updatedOn'],
    [Collection.metalsPowderCoatingPowder]: ['id', 'name', 'hexValue'],
    [Collection.metalsPressBrakeBendRadius]: ['id', 'value'],
    [Collection.metalsProcessStation]: ['id', 'taskType', 'name', 'taskIds', 'isDefault', 'labelPrinterId', 'allowedLabelTypes', 'promptTransportOnComplete', 'isTransportDestination', 'transparentForNextTaskActivation'],
    [Collection.metalsProduct]: ['id', 'qtyOnHand'],
    [Collection.metalsInventoryRack]: ['name', 'rows', 'columns', 'labelPrinterId'],
    [Collection.metalsInventoryBin]: ['rackId', 'rowIndex', 'columnIndex', 'productId', 'quantity'],
    [Collection.metalsProductBillOfMaterialsLineItem]: ['id', 'entityId', 'quantity', 'productVersionId', 'type'],
    [Collection.metalsProductVersion]: ['id', 'productId', 'partNumber', 'description', 'customerId', 'customerDescription', 'status', 'type', 'versionNumber', 'drawingResourceId', 'imageUrl', 'thumbnailImageUrl', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment', 'templateProjectId', 'quoteId', 'packUnitQuantity', 'packUnitType'],
    [Collection.obsessedEngineeringComponent]: ['partNumber', 'customerPartNumber', 'description', 'imageUrl', 'thumbnailImageUrl', 'qtyOnHand', 'units', 'type', 'metalsProductId', 'manufacturingCost'],
    [Collection.metalsProject]: ['id', 'name', 'code', 'customerId', 'createdById', 'createdOn', 'updatedById', 'purchaseOrderNumber', 'labelDescription', 'updatedOn', 'thumbnailDownloadUrl', 'status', 'completedOn', 'completedById', 'orderedProductId', 'sourceProductId', 'sourceProductVersionId', 'sourceTemplateProjectId'],
    [Collection.metalsTask]: ['id', 'type', 'nextTaskId', 'projectId', 'totalQuantity', 'completedQuantity', 'status', 'x', 'y', 'details', 'translatedDetails', 'files', 'activationCondition', 'isHot', 'hardwareItemIds', 'hardwareItemIdToQtyPerPartDict', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'materialId', 'approximateWidth', 'approximateHeight', 'bendRadiusIds', 'powderId'],
    [Collection.metalsTaskQuantityChangeEvent]: ['id', 'taskId', 'timestamp', 'quantityChange'],
    [Collection.metalsVendorHardwareData]: ['id', 'parentHardwareItemId', 'active', 'vendorId', 'sku', 'cost', 'packageQuantity', 'description', 'websiteUrl', 'lastSavedOn', 'lastSavedById'],
    [Collection.moldRepairRequest]: ['title', 'notes', 'followerIds', 'assetIds', 'files', 'lockoutTagOutRequired', 'updatedOn', 'updatedById', 'createdOn', 'createdById', 'taskNumber', 'status', 'eventsHistory', 'productId', 'moldId', 'sourceCheckupId'],
    [Collection.plasticsFinishingStation]: ['name', 'taskIds', 'labelPrinterId', 'controlEndpoint', 'canPrintCornerLabels', 'sortScheduleByDueDate', 'canAccessFHCornerLabelConfig', 'canPrintAsMoldedProductLabels', 'hideTasksFromIndex', 'trackCrewLabor', 'crewSupervisorIds'],
    [Collection.plasticsFinishingLaborSession]: ['employeeId', 'finishingTaskId', 'finishingStationId', 'orderedProductId', 'productId', 'productVersionId', 'isOpen', 'startedOn', 'endedOn', 'startedById', 'endedById', 'endReason', 'quantityAtStart', 'quantityAtEnd'],
    [Collection.kolorcansPackingConfig]: ['orderedProductIds', 'payloadJson', 'schemaVersion', 'packingAlgorithmVersion', 'readyForDashboard', 'archived', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.kolorcansPackingConfigProduct]: ['packingConfigId'],
    [Collection.plasticsFixture]: ['name', 'partNumber', 'description', 'rfidTagId', 'stlDownloadUrl', 'tagIds', 'favorite', 'lastSavedOn', 'lastSavedById'],
    [Collection.plasticsHardwareItem]: ['partNumber', 'description', 'imageUrl', 'thumbnailImageUrl', 'qtyOnHand', 'units', 'safetyDataSheetResourceId', 'defaultVendorHardwareDataId', 'lastSavedOn', 'lastSavedById', 'type', 'status', 'isUnitCounted', 'inventoryControlled', 'surchargeHistory'],
    [Collection.plasticsInventoryArea]: ['name', 'labelPrinterId'],
    [Collection.plasticsInventoryLocation]: ['name', 'binRows', 'inventoryAreaId', 'index'],
    [Collection.plasticsInventoryLocationBin]: ['inventoryLocationId', 'inventoryAreaId', 'rowIndex', 'index', 'size', 'hardwareItemId', 'quantity'],
    [Collection.plasticsKit]: ['code', 'productId', 'moldingTaskId', 'productVersionId', 'productCount', 'items', 'createdOn', 'createdById', 'checkedOutById', 'checkedOutOn'],
    [Collection.plasticsKittingArea]: ['name', 'labelPrinterId'],
    [Collection.plasticsKittingLocation]: ['name', 'binRows', 'binColumns', 'kittingAreaId', 'index'],
    [Collection.plasticsKittingLocationBin]: ['kittingLocationId', 'kittingAreaId', 'rowIndex', 'colIndex', 'kitId'],
    [Collection.plasticsLabelInstance]: ['type', 'printDate', 'labelPrinterId', 'size', 'printQuantity', 'seljanPartNumber', 'customerName', 'customerPartNumber', 'description', 'quantity', 'purchaseOrderNumber', 'salesOrderNumber', 'qualityAlertIds', 'labelCode', 'productId', 'imageType', 'orderedProductId', 'productVersionId', 'customerId', 'partNumber', 'serialCode', 'rotoMachineId', 'armNumber', 'finishingStationId', 'lineItemData', 'productCount', 'boxCount', 'boxIndex', 'parsedDesign', 'parsedColor', 'addedHardwareData', 'split', 'projectId', 'projectCode', 'projectName', 'projectDescription', 'projectCreatorName', 'linearTaskList', 'hardwareItemId', 'vendorHardwareDataDescription', 'items', 'kitId', 'code', 'shippingAddress', 'shipmentId', 'carrierName', 'shipDate', 'orderId', 'orderNumber', 'customerPurchaseOrderNumber', 'createdById', 'lotTraceability', 'vendorPartNumber', 'arrowDirection', 'rackId', 'rackName', 'binId', 'binCode'],
    [Collection.plasticsMaterial]: ['identifier', 'description', 'imageUrl', 'thumbnailImageUrl', 'status', 'ingredients', 'mixingInstructions', 'qtyOnHand', 'createdbyId', 'createdOn', 'updatedById', 'updatedOn'],
    [Collection.plasticsMaterialMixingTask]: ['materialId', 'completedQuantity', 'releasedGroupId', 'totalQuantity', 'suggestedQuantity', 'orderedProductId', 'associatedProductId', 'associatedProductQuantity', 'updatedById', 'updatedOn', 'status', 'completedById', 'completedOn'],
    [Collection.plasticsMoldFamily]: ['createdOn', 'createdById', 'name', 'description', 'armAllocations'],
    [Collection.metalsProductSetup]: ['name', 'description', 'notes', 'imageUrl', 'thumbnailImageUrl', 'operations', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.toolRoomMachine]: ['name', 'description', 'imageUrl', 'thumbnailImageUrl', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.plasticsMoldingTask]: ['productId', 'moldId', 'totalQuantity', 'completedQuantity', 'orderedProductId', 'status', 'completedOn', 'note', 'kitNeeded', 'hotListOrderItemId'],
    [Collection.plasticsDailyProduction]: ['productionDate', 'dayStart', 'dayEnd', 'partsMolded', 'partsRejected', 'partsFinished', 'backlogParts', 'backlogSource', 'computedOn'],
    [Collection.plasticsOrder]: ['orderNumber', 'customerId', 'customerPurchaseOrderNumber', 'shippingAddressId', 'shippingContactId', 'pricingChecked', 'estimatingConfirmed', 'acknowledged', 'truckingNote', 'createdOn', 'createdById', 'updatedOn', 'updatedById', 'files'],
    [Collection.plasticsOrderedProduct]: ['plasticsOrderId', 'productId', 'quantity', 'customerId', 'dueDate', 'salesOrderNumber', 'note', 'productionNote', 'translatedProductionNote', 'status', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'changeLog', 'additionalCosts', 'productionPlanComplete', 'inventoryPlanComplete', 'flagged', 'scheduledReleaseWeeks', 'pricingChecked', 'estimatingConfirmed'],
    [Collection.plasticsOrderReviewResearch]: ['plasticsOrderId', 'orderedProductId', 'sourceFileId', 'analyzedOn', 'analysisVersion', 'result'],
    [Collection.plasticsOrderReviewRulebook]: ['kind', 'version', 'contentHash', 'contentJson', 'createdBy', 'createdOn'],
    [Collection.plasticsOrderIntake]: ['customerId', 'customerPurchaseOrderNumber', 'sourceLineKey', 'status', 'contentHash', 'contentJson', 'updatedOn'],
    [Collection.plasticsOrderIntakeApproval]: ['intakeId', 'contentHash', 'approvedById', 'approvedOn', 'kind'],
    [Collection.plasticsOrderCase]: ['customerId', 'customerName', 'customerPurchaseOrderNumber', 'status', 'sourceKind', 'receivedOn', 'messageId', 'threadId', 'sourceFileId', 'sourceSha256', 'existingOrderId', 'contentHash', 'contentJson', 'decisionsJson', 'updatedOn', 'updatedById'],
    [Collection.plasticsPurchaseRequisition]: ['hardwareId', 'plasticsOrderedProductId', 'quantity', 'neededQuantity', 'groupKey', 'purchaseRequestId', 'noOrderNeeded'],
    [Collection.plasticsCalendarEvent]: ['eventDate', 'eventTimeSet', 'title', 'type', 'notes'],
    [Collection.plasticsColorCode]: ['code', 'name', 'hexValue', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.plasticsFinishingTask]: ['productId', 'quantityToFinish', 'completedQuantity', 'note', 'warningMarkdown', 'translatedWarningMarkdown', 'status', 'activeWork', 'updatedById', 'updatedOn', 'completedById', 'completedOn', 'productVersionIdWhenCompleted', 'completedAtFinishingStationId', 'labelImageType', 'purchaseOrderNumber', 'salesOrderNumber', 'labelProductDescription', 'endCustomerId', 'orderedProductId', 'hotListOrderItemId'],
    [Collection.plasticsQualityAlert]: ['title', 'markdown', 'translatedTitle', 'translatedMarkdown'],
    [Collection.plasticsScrapInstance]: ['productId', 'scrapReasonIds', 'imageDownloadUrls', 'otherReasonForScrap', 'createdOn', 'createdById'],
    [Collection.plasticsScrapReason]: ['name', 'description', 'active'],
    [Collection.plasticsSkid]: ['orderedProductId', 'shipmentId', 'productId', 'productVersionId', 'packingLabelId', 'quantity', 'customerId', 'salesOrderNumber', 'purchaseOrderNumber', 'createdById', 'createdOn', 'shippedById', 'shippedOn', 'length', 'width', 'height', 'weight', 'uploadedFiles', 'orderedProductCompletedOn'],
    [Collection.plasticsShipment]: ['status', 'customerId', 'carrierId', 'shippingMethod', 'shipDate', 'billingAddressId', 'billingContactId', 'shippingAddressId', 'shippingContactId', 'upsShipmentId', 'scheduledByCustomer', 'packingSlipNote', 'freightCharge', 'freightReference', 'lineItems', 'comments', 'files', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.upsShipment]: ['status', 'supersedesId', 'customerId', 'addressId', 'contactId', 'shipFromCustomerId', 'shipFromAddressId', 'shipFromContactId', 'shipToResidential', 'shipFromResidential', 'shipperNumber', 'shipmentIdentificationNumber', 'packages', 'serviceCode', 'serviceDescription', 'billing', 'referenceNumbers', 'notifyRecipients', 'publishedCost', 'negotiatedCost', 'publishedBreakdown', 'negotiatedBreakdown', 'processedOn', 'lastTrackedOn', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.plasticsStaticLabel]: ['createdOn', 'createdById', 'updatedOn', 'updatedById', 'zpl', 'name'],
    [Collection.procedureInstance]: ['title', 'runId', 'procedureId', 'procedureVersionId', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'completedById', 'assignedToId', 'completedOn', 'status', 'totalStepCount', 'completedStepCount', 'assetId', 'scheduled', 'followerIds'],
    [Collection.procedureInstanceStep]: ['procedureId', 'versionId', 'instanceId', 'stepId', 'status', 'statusNote', 'statusUpdatedOn', 'statusUpdatedById', 'userInputs'],
    [Collection.procedureVersion]: ['procedureId', 'name', 'description', 'status', 'versionNumber', 'prefaceMarkdown', 'steps', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment'],
    [Collection.procedure]: ['tagIds', 'favorite', 'code', 'type'],
    [Collection.masterProcedure]: ['code'],
    [Collection.masterProcedureVersion]: ['masterProcedureId', 'name', 'description', 'steps', 'status', 'versionNumber', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment'],
    [Collection.productInstance]: ['productId', 'productVersionId', 'scheduledProductId', 'createdOn', 'createdById', 'outcome', 'rejectedPartReasons', 'machineId', 'armId'],
    [Collection.productVersionMoldConfig]: ['productVersionId', 'ovenTime', 'ovenTemperature', 'armSpeed', 'plateSpeed', 'reversingTime', 'airOnMold', 'airTime', 'airPressurePsi', 'preCoolingTime', 'coolingTime'],
    [Collection.productVersion]: ['productId', 'fixtureId', 'robotProgramId', 'plasticsProductCostModelId', 'status', 'versionNumber', 'partNumber', 'type', 'imageUrl', 'thumbnailImageUrl', 'stlUrl', 'description', 'customerPartNumber', 'customerId', 'drawingResourceId', 'createdById', 'createdOn', 'updatedById', 'updatedOn', 'releasedById', 'releasedOn', 'releaseComment', 'defaultFinishingStationId', 'finishingProcedureId', 'requiresAsMoldedLabel', 'moldFamilyId', 'groceryStoreProduct'],
    [Collection.product]: ['qtyOnHand'],
    [Collection.resource]: ['resourceNumber', 'name', 'description', 'downloadUrl', 'type', 'status', 'successorResourceId', 'createdOn', 'createdById'],
    [Collection.rotoMachine]: ['name', 'arms', 'factoryNodeId', 'labelPrinterId', 'approximateDailyCycleCount'],
    [Collection.rotoMachineArmTally]: ['machineId', 'armId', 'timestamp'],
    [Collection.scheduledProcedure]: ['procedureId', 'title', 'createdById', 'createdOn', 'assignedToId', 'nextExecutionDate', 'intervalDayCount', 'assetId'],
    [Collection.suncastSnapshot]: [
        'captureDate', 'capturedOn', 'createdOn', 'originalUrl', 'undistortedUrl', 'status', 'rowCount', 'errorMessage',
        'productEntriesJson',
        'completedById', 'completedByName', 'completedOn', 'updatedById', 'updatedByName', 'updatedOn',
    ],
    [Collection.resinSiloReading]: [
        'capturedOn', 'createdOn',
        'originalUrl',
        'resinSiloAValue', 'resinSiloBValue',
    ],
    [Collection.resinSiloCaptureRequest]: [
        'status', 'requestedOn', 'requestedByName', 'completedOn', 'message', 'readingId',
    ],
    [Collection.triumvirateBarcodeScanningSession]: ['title', 'date', 'scans', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.sheetMetalJob]: ['name', 'customerId', 'status', 'metalsProjectId', 'notes', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.sheetMetalSourceFile]: ['jobId', 'name', 'stepFileId', 'sizeBytes', 'status', 'partsFound', 'solidsSkipped', 'error', 'uploadedOn', 'uploadedById'],
    [Collection.sheetMetalPart]: ['jobId', 'sourceFileId', 'solidIndex', 'name', 'quantity', 'duplicateSignature', 'mergedSiblings', 'thicknessIn', 'kFactor', 'bendCount', 'bends', 'blankLengthIn', 'blankWidthIn', 'status', 'error', 'configFileId', 'dxfFileId', 'bendConfigFileId', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.tag]: ['name', 'category'],
    [Collection.user]: ['email', 'phoneNumber', 'firstName', 'lastName', 'imageUrl', 'thumbnailImageUrl', 'updatedOn', 'createdOn', 'abilities', 'active', 'badgeId', 'userType', 'primaryLanguage', 'workspaceUserId'],
    [Collection.userNotification]: ['userId', 'description', 'associatedUrl', 'createdOn', 'acknowledged', 'acknowledgedOn'],
    [Collection.vacationEvent]: ['employeeType', 'employeeId', 'employeeUserId', 'customEmployeeName', 'customEmployeeTitle', 'startDate', 'endDate', 'notes', 'createdById', 'createdOn', 'updatedById', 'updatedOn'],
    [Collection.employee]: ['firstName', 'lastName', 'preferredName', 'imageUrl', 'thumbnailImageUrl', 'jobTitle', 'managerEmployeeId', 'userId', 'status', 'employmentType', 'shift', 'hireDate', 'endDate', 'endReason', 'workEmail', 'workPhone', 'personalEmail', 'personalPhone', 'addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelationship', 'dateOfBirth', 'notes', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.employeeRecord]: ['employeeId', 'type', 'title', 'description', 'effectiveDate', 'expirationDate', 'previousValue', 'newValue', 'files', 'createdOn', 'createdById', 'updatedOn', 'updatedById'],
    [Collection.vendor]: ['name', 'imageUrl', 'thumbnailImageUrl', 'purchasingEmail', 'isPlasticsVendor', 'isMetalsVendor', 'isC3Vendor', 'isAutomationVendor', 'isInjectionMoldingVendor', 'metalsQuoteRequestMaterialTypes', 'leadTimeBusinessDays', 'lastSavedOn', 'lastSavedById'],
};

const APPWRITE_DATE_FIELDS: Partial<Record<Collection, string[]>> = {
    [Collection.email]: ['createdOn'],
    [Collection.sheetMetalJob]: ["createdOn", "updatedOn"],
    [Collection.sheetMetalSourceFile]: ["uploadedOn"],
    [Collection.sheetMetalPart]: ["createdOn", "updatedOn"],
    [Collection.kolorcansPackingConfig]: ['createdOn', 'updatedOn'],
    [Collection.metalsQuote]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.customerAddress]: ["createdOn", "updatedOn"],
    [Collection.plasticsHardwareItemShipmentItem]: ["createdOn"],
    [Collection.plasticsHardwareItemShipmentDamageItem]: ["createdOn"],
    [Collection.obsessedEngineeringComponentShipmentItem]: ["createdOn"],
    [Collection.obsessedEngineeringComponentShipmentDamageItem]: ["createdOn"],
    [Collection.obsessedEngineeringInvoice]: ["createdOn", "releasedOn"],
    [Collection.plasticsShipment]: ["shipDate", "createdOn", "updatedOn"],
    [Collection.upsShipment]: ["processedOn", "lastTrackedOn", "createdOn", "updatedOn"],
    [Collection.labelPrinter]: ["lastCommunicationTimestamp"],
    [Collection.factoryAsset]: ["createdOn"],
    [Collection.resourceSearchPackage]: ["updatedOn"],
    [Collection.assetHistoryEvent]: ["createdOn"],
    [Collection.factoryNode]: ["lastCommunication", "startUpTime"],
    [Collection.factoryNodeConfig]: ["lastSaved"],
    [Collection.robotProgramGroup]: ["updatedOn", "createdOn"],
    [Collection.robotProgram]: ["releasedOn", "updatedOn"],
    [Collection.plasticsMold]: ["createdOn", "updatedOn"],
    [Collection.plasticsMoldHistoryEvent]: ["occurredOn", "createdOn", "updatedOn"],
    [Collection.plasticsQualityAlertInspection]: ["createdOn"],
    [Collection.qualityAlertViewTally]: ["timestamp"],
    [Collection.purchaseRequestList]: ["createdOn"],
    [Collection.purchaseRequest]: ["canceledOn", "completedOn", "createdOn", "needDate", "noteUpdatedOn", "plannedOrderDate", "readiedOn"],
    [Collection.purchaseOrder]: ["expectedArrivalDate", "orderedOn", "receivedOn"],
    [Collection.c3HardwareItem]: ["lastSavedOn"],
    [Collection.c3VendorHardwareData]: ["lastSavedOn"],
    [Collection.c3Report]: ["createdOn"],
    [Collection.c3InventoryThresholdReport]: ["notifiedOn"],
    [Collection.vendorHardwareDataPriceHistory]: ["createdOn"],
    [Collection.c3ProductVersion]: ["createdOn", "releasedOn", "updatedOn"],
    [Collection.c3Graphic]: ["createdOn"],
    [Collection.c3GraphicVersion]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.c3GraphicResource]: ["createdOn"],
    [Collection.printingProduct]: ['createdOn'],
    [Collection.printingProductVersion]: ['createdOn', 'updatedOn', 'releasedOn'],
    [Collection.printingProductFile]: ['createdOn'],
    [Collection.motionmasterProgram]: ['createdOn'],
    [Collection.motionmasterProgramVersion]: ['createdOn', 'updatedOn', 'releasedOn'],
    [Collection.motionmasterTable]: ['measuredOn', 'surfaceMeasuredOn', 'updatedOn'],
    [Collection.injectionMoldingProductVersion]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.injectionMoldingProductionEvent]: ['occurredOn'],
    [Collection.injectionMoldingHardwareItem]: ["createdOn", "updatedOn"],
    [Collection.injectionMoldingMaterial]: ["createdOn", "updatedOn"],
    [Collection.injectionMoldingMold]: ['createdOn', 'updatedOn'],
    [Collection.injectionMoldingMoldSetup]: ['createdOn', 'updatedOn', 'releasedOn', 'verifiedOn'],
    [Collection.injectionMoldingVendorHardwareData]: ["lastSavedOn"],
    [Collection.plasticsManufacturingRates]: ["createdOn", "updatedOn"],
    [Collection.plasticsManufacturingRatesVersion]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.quoteStudio]: ["createdOn", "updatedOn"],
    [Collection.quoteStudioCostModel]: ["createdOn", "updatedOn"],
    [Collection.quoteStudioCostModelVersion]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.quoteStudioQuote]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.plasticsColorCode]: ["createdOn", "updatedOn"],
    [Collection.softwareRequest]: ["createdOn", "updatedOn"],
    [Collection.hotListItem]: ["createdOn", "updatedOn"],
    [Collection.hotListOrderItem]: ["createdOn", "updatedOn", "resolvedOn", "releasedOn", "hotDate"],
    [Collection.smartJeffProject]: ["createdOn", "updatedOn", "completedOn"],
    [Collection.commonProject]: ["createdOn", "updatedOn"],
    [Collection.commonProjectUpdate]: ["timestamp"],
    [Collection.capitalizationProject]: ["createdOn", "updatedOn", "completionDate"],
    [Collection.factoryMoveRequest]: ["deadline", "createdOn", "updatedOn", "completedOn", "canceledOn"],
    [Collection.employee]: ["hireDate", "endDate", "dateOfBirth", "createdOn", "updatedOn"],
    [Collection.employeeRecord]: ["effectiveDate", "expirationDate", "createdOn", "updatedOn"],
    [Collection.plasticsDailyProduction]: ["productionDate", "dayStart", "dayEnd", "computedOn"],
    [Collection.plasticsFinishingLaborSession]: ["startedOn", "endedOn"],
    [Collection.softwareTask]: ["createdOn", "updatedOn"],
    [Collection.knowledgeDocument]: ["createdOn", "updatedOn"],
    [Collection.engineeringUpdateMeeting]: ["meetingDate"],
    [Collection.financeEntry]: ["createdOn", "updatedOn"],
    [Collection.overtimeSummaryWeek]: ["weekOf", "createdOn", "updatedOn"],
    [Collection.directChat]: ["createdOn", "updatedOn"],
    [Collection.directChatMessage]: ["createdOn"],
    [Collection.userTimeSession]: ["date", "updatedOn"],
    [Collection.cronJob]: ["timestamp"],
    [Collection.gmailUserLogin]: ["time"],
    [Collection.gmailUserDevice]: ["createdOn", "lastSync"],
    [Collection.databaseBackupRecord]: ["createdOn"],
    [Collection.storageBackupRecord]: ["createdOn"],
    [Collection.deviceDocumentReads]: ["timestamp"],
    [Collection.cribbageGame]: ["date"],
    [Collection.stampingTask]: ["completedOn", "updatedOn"],
    [Collection.schematicVersion]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.suncastSnapshot]: ["capturedOn", "createdOn", "completedOn", "updatedOn"],
    [Collection.resinSiloReading]: ["capturedOn", "createdOn"],
    [Collection.resinSiloCaptureRequest]: ["requestedOn", "completedOn"],
    [Collection.triumvirateBarcodeScanningSession]: ["date", "createdOn", "updatedOn"],
    [Collection.automationHardwareItem]: ["lastSavedOn"],
    [Collection.automationVendorHardwareData]: ["lastSavedOn"],
    [Collection.automationFootprintVersion]: ["createdOn", "updatedOn", "releasedOn"],
    [Collection.engineeringProjectEngineer]: ["createdOn"],
    [Collection.engineeringProject]: ["createdOn", "updatedOn"],
    [Collection.engineeringFileProject]: ['createdOn', 'updatedOn'],
    [Collection.engineeringFileRevision]: ['createdOn', 'updatedOn', 'releasedOn'],
    [Collection.plasticsRework]: ["returnedOn", "reworkedOn", "createdOn", "updatedOn"],
    [Collection.plasticsOrderReviewResearch]: ['analyzedOn'],
};

const APPWRITE_JSON_FIELDS: Partial<Record<Collection, string[]>> = {
    [Collection.metalsQuote]: ["sheetSizes", "hardwareLines", "processingLines", "priceBreakLines", "summary", "attachments", "scans"],
    [Collection.upsShipment]: ["billing", "publishedBreakdown", "negotiatedBreakdown"],
    [Collection.robotProgram]: ["sceneSetup"],
    [Collection.directChat]: ["lastMessage", "typingUsers"],
    [Collection.gmailUserLogin]: ["location"],
    [Collection.databaseBackupRecord]: ["collectionDocuments", "collectionReadTimeDict"],
    [Collection.trendsShareLink]: ["curveColorsDict"],
    [Collection.obsessedEngineeringInvoice]: ["snapshot"],
    [Collection.plasticsOrderReviewResearch]: ['result'],
};

const APPWRITE_JSON_ARRAY_FIELDS: Partial<Record<Collection, string[]>> = {
    [Collection.plasticsHardwareItem]: ["surchargeHistory"],
    [Collection.plasticsShipment]: ["lineItems", "comments", "files"],
    [Collection.upsShipment]: ["packages", "notifyRecipients"],
    [Collection.purchaseRequest]: ["changeLog", "comments", "files"],
    [Collection.purchaseRequestArrival]: ["requestData"],
    [Collection.purchaseOrderChange]: ["requestData"],
    [Collection.purchaseRequestUnreadied]: ["requestData"],
    [Collection.smartJeffProject]: ["files", "updates"],
    [Collection.commonProjectUpdate]: ["files"],
    [Collection.capitalizationProject]: ["pictures"],
    [Collection.overtimeSummaryWeek]: ["entries"],
    [Collection.factoryMoveRequest]: ["files", "linkedItems"],
    [Collection.employeeRecord]: ["files"],
    [Collection.softwareTask]: ["uploadedFiles"],
    [Collection.plasticsHardwareItemShipmentDamageItem]: ["uploadedFiles"],
    [Collection.obsessedEngineeringComponentShipmentDamageItem]: ["uploadedFiles"],
    [Collection.directChat]: ["files", "messages"],
    [Collection.automationFootprintVersion]: ["shapes", "terminals"],
    [Collection.schematicVersion]: ["nodes", "wires"],
    [Collection.factoryNodeConfig]: ["devices", "virtualChannels", "screenConfigs", "constantConfigs", "interfaceConfigs", "enumConfigs"],
    [Collection.triumvirateBarcodeScanningSession]: ["scans"],
    [Collection.plasticsManufacturingRatesVersion]: ["materialCategoryRates"],
    // the recipe: each variation (with its hardware rows and ops) is one JSON string
    [Collection.quoteStudioCostModelVersion]: ["variations"],
    // each quote line (with its breaks and order bounds) and each tooling line is one JSON string
    [Collection.quoteStudioQuote]: ["lines", "tooling", "attachments", "terms", "statusHistory"],
    // Bend summaries are objects, so they cross as one JSON string per row
    [Collection.sheetMetalPart]: ["bends"],
    [Collection.plasticsMoldFamily]: ["armAllocations"],
    [Collection.plasticsMoldHistoryEvent]: ['files', 'changeLog'],
    [Collection.metalsProductSetup]: ["operations"],
    [Collection.injectionMoldingMold]: ['specifications', 'files', 'changeLog'],
    [Collection.injectionMoldingMoldSetup]: ['parameters', 'files', 'changeLog'],
    // each program step (with its path points) is one JSON string
    [Collection.robotProgram]: ["steps"],
};

// -------- conversion at the boundary --------

export function pickAppwriteFields(source: any, collection: Collection, includeId = false): any {
    const out: any = {};
    if (includeId) {
        out.id = source?.$id ?? source?.id ?? '';
    }
    for (const key of APPWRITE_COLLECTION_FIELDS[collection] ?? []) {
        if (Object.prototype.hasOwnProperty.call(source ?? {}, key)) {
            out[key] = source[key];
        }
    }
    return out;
}

function toAppwriteJsonArray(value: any): string[] {
    return (Array.isArray(value) ? value : [])
        .filter((item: any) => !(typeof item === 'string' && item.trim() === ''))
        .map((item: any) => typeof item === 'string' ? item : JSON.stringify(item))
        .filter((item: string | undefined): item is string => item !== undefined);
}

function fromAppwriteJsonArray(value: any): any[] {
    return (Array.isArray(value) ? value : [])
        .filter((item: any) => !(typeof item === 'string' && item.trim() === ''))
        .map((item: any) => typeof item === 'string' ? JSON.parse(item) : item);
}

function fromAppwriteLotTraceability(value: unknown): Array<{ key: string; value: string }> {
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (!Array.isArray(parsed)) return [];
        return parsed.flatMap((entry) => {
            if (!entry || typeof entry !== 'object') return [];
            const key = String((entry as any).key ?? '').trim();
            const traceValue = String((entry as any).value ?? '').trim();
            return key && traceValue ? [{ key, value: traceValue }] : [];
        });
    } catch (error) {
        console.warn('[appwrite conversion] invalid Injection Molding lot traceability JSON', error);
        return [];
    }
}

/**
 * Parse the customer `upsAccounts` column into a plain string[] of account numbers. Tolerates the
 * legacy shape where each element was a stringified `{ id, label, accountNumber, isDefault }` object
 * (or the whole array was a single stringified string) — those are collapsed to their accountNumber
 * so old customers keep working without a data migration; each rewrites to the clean string[] on its
 * next save. A plain account-number string (the new shape) is kept verbatim (never JSON-parsed, so
 * numeric-looking numbers aren't coerced to a number).
 */
function fromAppwriteUpsAccounts(value: any): string[] {
    let items: any[];
    if (Array.isArray(value)) {
        items = value;
    } else if (typeof value === 'string' && value.trim() !== '') {
        try {
            const parsed = JSON.parse(value);
            items = Array.isArray(parsed) ? parsed : [];
        } catch {
            items = [];
        }
    } else {
        items = [];
    }

    // Normalize each element to { accountNumber, isDefault }, tolerating a legacy object (or
    // stringified object) and the new plain-string shape.
    const normalized = items
        .map((item: any) => {
            if (item && typeof item === 'object') {
                return { accountNumber: String(item.accountNumber ?? '').trim(), isDefault: !!item.isDefault };
            }
            if (typeof item === 'string') {
                const s = item.trim();
                if (s.startsWith('{')) {
                    try { const o = JSON.parse(s); return { accountNumber: String(o.accountNumber ?? '').trim(), isDefault: !!o.isDefault }; }
                    catch { return { accountNumber: '', isDefault: false }; }
                }
                return { accountNumber: s, isDefault: false }; // plain account number (new shape)
            }
            return { accountNumber: '', isDefault: false };
        })
        .filter(a => a.accountNumber.length > 0);

    // First entry is the default now, so float a legacy isDefault to the front (stable sort keeps
    // the new shape's order untouched). Then dedupe, keeping the first occurrence.
    const ordered = [...normalized].sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
    const seen = new Set<string>();
    return ordered.map(a => a.accountNumber).filter(n => (seen.has(n) ? false : (seen.add(n), true)));
}

export function toAppwrite(collection: Collection, data: any): any {
    if (collection === Collection.plasticsInvoice) {
        return {
            plasticsShipmentId: data.plasticsShipmentId || null, customerId: data.customerId,
            status: data.status, activeKey: data.activeKey, invoiceNumber: data.invoiceNumber || null,
            updatedOn: new Date(data.updatedOn).toISOString(),
            payloadJson: JSON.stringify(data),
        };
    }
    if ([Collection.plasticsMoldCheckup, Collection.plasticsMoldNotice, Collection.plasticsMoldCheckupCommand].includes(collection)) {
        const { id, ...payload } = data;
        const out: any = { payloadJson: JSON.stringify(payload), createdOn: new Date(data.createdOn).toISOString() };
        if (collection !== Collection.plasticsMoldCheckupCommand) out.moldId = data.moldId;
        if (collection === Collection.plasticsMoldCheckup) {
            out.status = data.status;
            out.sourceMachineId = data.sourceMachineId;
        }
        return out;
    }
    if (collection === Collection.globalVariables) {
        const now = new Date().toISOString();
        if (typeof data?.valueJson === 'string') {
            return {
                kind: data.kind ?? '',
                valueJson: data.valueJson,
                schemaVersion: data.schemaVersion ?? 1,
                updatedOn: data.updatedOn ? new Date(data.updatedOn).toISOString() : now,
            };
        }
        const value = { ...(data ?? {}) };
        delete value.id;
        delete value.kind;
        delete value.schemaVersion;
        delete value.updatedOn;
        return {
            kind: data?.kind ?? '',
            valueJson: JSON.stringify(value),
            schemaVersion: data?.schemaVersion ?? 1,
            updatedOn: now,
        };
    }
    if (collection === Collection.c3Report) {
        const out = pickAppwriteFields(data, collection);
        out.payloadJson = JSON.stringify(data?.payload ?? {});
        out.createdOn = data?.createdOn
            ? new Date(data.createdOn).toISOString()
            : null;
        return out;
    }
    const rest = pickAppwriteFields(data, collection);
    if (collection === Collection.plasticsHardwareItem) {
        return {
            partNumber: rest.partNumber ?? '',
            description: rest.description ?? '',
            imageUrl: rest.imageUrl ?? '',
            thumbnailImageUrl: rest.thumbnailImageUrl ?? '',
            qtyOnHand: rest.qtyOnHand ?? 0,
            units: rest.units ?? null,
            safetyDataSheetResourceId: rest.safetyDataSheetResourceId ?? '',
            defaultVendorHardwareDataId: rest.defaultVendorHardwareDataId ?? '',
            lastSavedOn: rest.lastSavedOn ? new Date(rest.lastSavedOn).toISOString() : null,
            lastSavedById: rest.lastSavedById ?? '',
            type: rest.type ?? null,
            status: rest.status ?? 'Active',
            isUnitCounted: rest.isUnitCounted ?? false,
            inventoryControlled: rest.inventoryControlled ?? false,
            surchargeHistory: toAppwriteJsonArray(rest.surchargeHistory),
        };
    }
    if (collection === Collection.user) {
        const out = pickAppwriteFields(data, collection);
        out.abilities = JSON.stringify(data.abilities ?? {});
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.vendor) {
        const out = pickAppwriteFields(data, collection);
        out.isInjectionMoldingVendor = data.isInjectionMoldingVendor ?? false;
        out.metalsQuoteRequestMaterialTypes = JSON.stringify(data.metalsQuoteRequestMaterialTypes ?? {});
        out.lastSavedOn = data.lastSavedOn ? new Date(data.lastSavedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.contact) {
        const out = pickAppwriteFields(data, collection);
        out.phoneNumbers = JSON.stringify(data.phoneNumbers ?? []);
        out.primaryPhoneNumber = data.primaryPhoneNumber == null ? null : JSON.stringify(data.primaryPhoneNumber);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.customerAddress) {
        // Flatten the nested `address` object into queryable columns.
        const a = data.address ?? {};
        return {
            customerId: data.customerId ?? '',
            label: data.label ?? '',
            line1: a.line1 ?? '',
            line2: a.line2 ?? '',
            city: a.city ?? '',
            region: a.region ?? '',
            postalCode: a.postalCode ?? '',
            country: a.country ?? '',
            isShippingAddress: data.isShippingAddress ?? false,
            isBillingAddress: data.isBillingAddress ?? false,
            isDefault: data.isDefault ?? false,
            isOneTimeUse: data.isOneTimeUse ?? false,
            receiverName: data.receiverName ?? '',
            contactId: data.contactId ?? '',
            createdOn: data.createdOn ? new Date(data.createdOn).toISOString() : null,
            createdById: data.createdById ?? '',
            updatedOn: data.updatedOn ? new Date(data.updatedOn).toISOString() : null,
            updatedById: data.updatedById ?? '',
        };
    }
    if (collection === Collection.customer) {
        const legacyPartNumberPrefix = data.partNumberPrefix ?? '';
        return {
            name: data.name ?? '',
            // Omit terms for older callers that do not carry the field, preserving stored terms.
            ...(data.terms !== undefined ? { terms: data.terms ?? '' } : {}),
            imageUrl: data.imageUrl ?? '',
            thumbnailImageUrl: data.thumbnailImageUrl ?? '',
            // Keep writing the legacy column until every deployed client has moved to the
            // department-specific fields and the column can be removed safely.
            partNumberPrefix: legacyPartNumberPrefix
                || data.plasticsPartNumberPrefix
                || data.metalsPartNumberPrefix
                || data.c3PartNumberPrefix
                || data.injectionMoldingPartNumberPrefix
                || '',
            plasticsPartNumberPrefix: data.plasticsPartNumberPrefix
                || (data.isPlasticsCustomer ? legacyPartNumberPrefix : ''),
            metalsPartNumberPrefix: data.metalsPartNumberPrefix
                || (data.isMetalsCustomer ? legacyPartNumberPrefix : ''),
            c3PartNumberPrefix: data.c3PartNumberPrefix
                || (data.isC3Customer ? legacyPartNumberPrefix : ''),
            injectionMoldingPartNumberPrefix: data.injectionMoldingPartNumberPrefix ?? '',
            isMetalsCustomer: data.isMetalsCustomer ?? false,
            isPlasticsCustomer: data.isPlasticsCustomer ?? false,
            isC3Customer: data.isC3Customer ?? false,
            isInjectionMoldingCustomer: data.isInjectionMoldingCustomer ?? false,
            lastSavedOn: data.lastSavedOn ? new Date(data.lastSavedOn).toISOString() : null,
            lastSavedById: data.lastSavedById ?? '',
            labelImageType: data.labelImageType ?? null,
            // addresses moved to the customer-address collection; upsAccounts is a string[] of
            // plain account numbers (kept as-is; empties dropped).
            upsAccounts: toAppwriteJsonArray(data.upsAccounts ?? []),
            // Only when the caller carries documents: upsert keeps a column it is not sent, so the
            // edit dialog and older clients leave a customer's documents alone.
            ...(data.files !== undefined ? { files: toAppwriteJsonArray(data.files) } : {}),
            // The current note pointer is written only by the versioned note command.
        };
    }
    if (collection === Collection.customerNoteVersion) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        return out;
    }
    // Foundation: Product, Resource, Tag.
    if (collection === Collection.product) {
        return {
            qtyOnHand: rest.qtyOnHand ?? 0,
        };
    }
    if (collection === Collection.resource) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.tag) {
        return pickAppwriteFields(data, collection);
    }
    // Product-version core: ProductVersion, ProductVersionMoldConfig,
    // PlasticsMold, PlasticsMoldFamily, PlasticsFinishingStation.
    if (collection === Collection.productVersion) {
        const out = pickAppwriteFields(data, collection);
        out.plasticsProductCostModelId = data.plasticsProductCostModelId ?? '';
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.releasedOn = data.releasedOn ? new Date(data.releasedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.productVersionMoldConfig) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.plasticsMold) {
        const out = pickAppwriteFields(data, collection);
        out.files = toAppwriteJsonArray(data.files);
        for (const key of ['checkupInterval', 'lastCheckupCycleCount', 'openCheckupId']) {
            if (Object.prototype.hasOwnProperty.call(data, key)) out[key] = data[key];
        }
        if (Object.prototype.hasOwnProperty.call(data, 'placement')) out.placementJson = JSON.stringify(data.placement ?? null);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.moldArmCycle) {
        const out = pickAppwriteFields(data, collection);
        out.completedOn = data.completedOn ? new Date(data.completedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsMoldFamily) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.armAllocations = toAppwriteJsonArray(data.armAllocations ?? []);
        return out;
    }
    if (collection === Collection.metalsProductSetup) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.operations = toAppwriteJsonArray(data.operations ?? []);
        return out;
    }
    if (collection === Collection.toolRoomMachine) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsFinishingStation) {
        return pickAppwriteFields(data, collection);
    }
    // BOM/material: BillOfMaterialsLineItem, PlasticsMaterial.
    if (collection === Collection.billOfMaterialsLineItem) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.plasticsMaterial) {
        const out = pickAppwriteFields(data, collection);
        out.ingredients = (data.ingredients ?? []).map((i: any) => JSON.stringify(i));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    // Quality + labels: PlasticsQualityAlert, PlasticsStaticLabel, 3 mappings.
    if (collection === Collection.plasticsQualityAlert) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.plasticsStaticLabel) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.mappingProductVersionToQualityAlert) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.mappingProductVersionToDockInspectionQualityAlert) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.mappingProductVersionToPlasticsStaticLabel) {
        return pickAppwriteFields(data, collection);
    }
    // View/edit-product dependants.
    if (collection === Collection.plasticsMoldingTask) {
        const out = pickAppwriteFields(data, collection);
        out.moldId = data.moldId ?? '';
        out.completedOn = data.completedOn ? new Date(data.completedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsSkid) {
        const out = pickAppwriteFields(data, collection);
        out.uploadedFiles = (data.uploadedFiles ?? []).map((f: any) => JSON.stringify(f));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.shippedOn = data.shippedOn ? new Date(data.shippedOn).toISOString() : null;
        out.orderedProductCompletedOn = data.orderedProductCompletedOn ? new Date(data.orderedProductCompletedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsOrder) {
        const out = pickAppwriteFields(data, collection);
        if (Object.prototype.hasOwnProperty.call(data, 'files')) {
            out.files = (data.files ?? []).map((f: any) => JSON.stringify(f));
        }
        if (Object.prototype.hasOwnProperty.call(data, 'createdOn')) {
            out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'updatedOn')) {
            out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        }
        return out;
    }
    if (collection === Collection.plasticsOrderedProduct) {
        const out = pickAppwriteFields(data, collection);
        if (Object.prototype.hasOwnProperty.call(data, 'changeLog')) {
            out.changeLog = (data.changeLog ?? []).map((entry: any) => JSON.stringify({
                ...entry,
                createdOn: entry.createdOn ? new Date(entry.createdOn).toISOString() : null,
            }));
        }
        if (Object.prototype.hasOwnProperty.call(data, 'additionalCosts')) {
            out.additionalCosts = (data.additionalCosts ?? []).map((entry: any) => JSON.stringify({
                label: entry.label,
                amount: entry.amount,
                createdOn: entry.createdOn ? new Date(entry.createdOn).toISOString() : null,
            }));
        }
        for (const key of ['dueDate', 'createdOn', 'updatedOn']) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                out[key] = data[key] ? new Date(data[key]).toISOString() : null;
            }
        }
        return out;
    }
    if (collection === Collection.plasticsCalendarEvent) {
        const out = pickAppwriteFields(data, collection);
        out.eventDate = data.eventDate ? new Date(data.eventDate).toISOString() : null;
        return out;
    }
    if (collection === Collection.dockInspectionInstance) {
        const out = pickAppwriteFields(data, collection);
        out.inspectionInstanceHistory = (data.inspectionInstanceHistory ?? []).map((item: any) => JSON.stringify({
            ...item,
            createdOn: item.createdOn ? new Date(item.createdOn).toISOString() : null,
            files: item.files ?? [],
        }));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsMaterialMixingTask) {
        const out = pickAppwriteFields(data, collection);
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.completedOn = data.completedOn ? new Date(data.completedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsFinishingTask) {
        const out = pickAppwriteFields(data, collection);
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.completedOn = data.completedOn ? new Date(data.completedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsKit) {
        const out = pickAppwriteFields(data, collection);
        out.items = (data.items ?? []).map((item: any) => JSON.stringify(item));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.checkedOutOn = data.checkedOutOn ? new Date(data.checkedOutOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsLabelInstance) {
        const out = pickAppwriteFields(data, collection);
        out.lineItemData = (data.lineItemData ?? []).map((item: any) => JSON.stringify(item));
        out.addedHardwareData = (data.addedHardwareData ?? []).map((item: any) => JSON.stringify(item));
        out.linearTaskList = (data.linearTaskList ?? []).map((item: any) => JSON.stringify(item));
        out.items = (data.items ?? []).map((item: any) => JSON.stringify(item));
        out.printDate = data.printDate ? new Date(data.printDate).toISOString() : null;
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'lotTraceability')) {
            out.lotTraceability = JSON.stringify(fromAppwriteLotTraceability(data.lotTraceability));
        }
        // Shipment-label-only field; serialize when present (other label types omit it).
        if (data.shipDate) out.shipDate = new Date(data.shipDate).toISOString();
        return out;
    }
    if (collection === Collection.rotoMachine) {
        const out = pickAppwriteFields(data, collection);
        out.arms = (data.arms ?? []).map((arm: any) => typeof arm === 'string' ? arm : JSON.stringify(arm));
        return out;
    }
    if (collection === Collection.rotoMachineArmTally) {
        const out = pickAppwriteFields(data, collection);
        out.timestamp = data.timestamp ? new Date(data.timestamp).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsScrapInstance) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsScrapReason) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.procedure) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.masterProcedure) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.masterProcedureVersion) {
        const out = pickAppwriteFields(data, collection);
        out.steps = JSON.stringify(data.steps ?? []);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.releasedOn = data.releasedOn ? new Date(data.releasedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.procedureInstance) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.completedOn = data.completedOn ? new Date(data.completedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.procedureInstanceStep) {
        const out = pickAppwriteFields(data, collection);
        out.statusUpdatedOn = data.statusUpdatedOn ? new Date(data.statusUpdatedOn).toISOString() : null;
        out.userInputs = JSON.stringify(Array.isArray(data.userInputs) ? data.userInputs : []);
        return out;
    }
    if (collection === Collection.procedureVersion) {
        const out = pickAppwriteFields(data, collection);
        out.steps = JSON.stringify(data.steps ?? []);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.releasedOn = data.releasedOn ? new Date(data.releasedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.plasticsInventoryLocationBin) {
        return pickAppwriteFields(data, collection);
    }
    if (
        collection === Collection.plasticsHardwareItemShipment ||
        collection === Collection.obsessedEngineeringComponentShipment
    ) {
        const out = pickAppwriteFields(data, collection);
        out.uploadedFiles = (data.uploadedFiles ?? []).map((f: any) => typeof f === 'string' ? f : JSON.stringify(f));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.receivedOn = data.receivedOn ? new Date(data.receivedOn).toISOString() : null;
        return out;
    }
    if (
        collection === Collection.plasticsInventoryArea ||
        collection === Collection.plasticsInventoryLocation ||
        collection === Collection.plasticsKittingArea ||
        collection === Collection.plasticsKittingLocation ||
        collection === Collection.plasticsKittingLocationBin
    ) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.moldRepairRequest || collection === Collection.maintenanceTask) {
        const out = pickAppwriteFields(data, collection);
        out.files = (data.files ?? []).map((f: any) => JSON.stringify(f));
        out.eventsHistory = (data.eventsHistory ?? []).map((event: any) => JSON.stringify({
            ...event,
            createdOn: event.createdOn ? new Date(event.createdOn).toISOString() : null,
        }));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.userNotification) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.acknowledgedOn = data.acknowledgedOn ? new Date(data.acknowledgedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.vacationEvent) {
        const out = pickAppwriteFields(data, collection);
        out.startDate = data.startDate ? new Date(data.startDate).toISOString() : null;
        out.endDate = data.endDate ? new Date(data.endDate).toISOString() : null;
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.scheduledProcedure) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.nextExecutionDate = data.nextExecutionDate ? new Date(data.nextExecutionDate).toISOString() : null;
        return out;
    }
    // View/edit-product additional dependants.
    if (collection === Collection.productInstance) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.complaintLog) {
        const out = pickAppwriteFields(data, collection);
        out.status = data.status || 'unresolved';
        out.uploadedFiles = (data.uploadedFiles ?? []).map((f: any) => JSON.stringify(f));
        out.comments = (data.comments ?? []).map((c: any) => JSON.stringify(c));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.complaintLogProduct) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.plasticsFixture) {
        const out = pickAppwriteFields(data, collection);
        out.lastSavedOn = data.lastSavedOn ? new Date(data.lastSavedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.vendorHardwareData) {
        const out = pickAppwriteFields(data, collection);
        out.lastSavedOn = data.lastSavedOn ? new Date(data.lastSavedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.metalsHardwareItem || collection === Collection.metalsVendorHardwareData) {
        const out = pickAppwriteFields(data, collection);
        out.lastSavedOn = data.lastSavedOn ? new Date(data.lastSavedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.metalsProcessStation) {
        return pickAppwriteFields(data, collection);
    }
    if (
        collection === Collection.metalsLaserMaterial ||
        collection === Collection.metalsPowderCoatingPowder ||
        collection === Collection.metalsPressBrakeBendRadius ||
        collection === Collection.metalsProduct ||
        collection === Collection.metalsProductBillOfMaterialsLineItem
    ) {
        return pickAppwriteFields(data, collection);
    }
    if (collection === Collection.metalsProductVersion) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.releasedOn = data.releasedOn ? new Date(data.releasedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.metalsProject) {
        const out = pickAppwriteFields(data, collection);
        out.sourceProductId = data.sourceProductId ?? '';
        out.sourceProductVersionId = data.sourceProductVersionId ?? '';
        out.sourceTemplateProjectId = data.sourceTemplateProjectId ?? '';
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.completedOn = data.completedOn ? new Date(data.completedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.metalsTask) {
        const out = pickAppwriteFields(data, collection);
        out.nextTaskId = data.nextTaskId ?? '';
        out.files = (data.files ?? []).map((file: any) => typeof file === 'string' ? file : JSON.stringify(file));
        out.hardwareItemIdToQtyPerPartDict = JSON.stringify(data.hardwareItemIdToQtyPerPartDict ?? {});
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.metalsTaskQuantityChangeEvent) {
        const out = pickAppwriteFields(data, collection);
        out.timestamp = data.timestamp ? new Date(data.timestamp).toISOString() : null;
        return out;
    }
    if (collection === Collection.metalsOrder) {
        const out = pickAppwriteFields(data, collection);
        out.files = (data.files ?? []).map((file: any) => typeof file === 'string' ? file : JSON.stringify(file));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.metalsOrderedProduct) {
        const out = pickAppwriteFields(data, collection);
        out.changeLog = (data.changeLog ?? []).map((entry: any) => typeof entry === 'string' ? entry : JSON.stringify({
            ...entry,
            createdOn: entry.createdOn ? new Date(entry.createdOn).toISOString() : null,
        }));
        out.dueDate = data.dueDate ? new Date(data.dueDate).toISOString() : null;
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.obsessedEngineeringComponent) {
        const out = pickAppwriteFields(data, collection);
        out.customerPartNumber = data.customerPartNumber ?? '';
        out.qtyOnHand = Number(data.qtyOnHand ?? 0);
        out.type = data.type ?? ObsessedEngineeringComponentType.CustomerSupplied;
        out.metalsProductId = out.type === ObsessedEngineeringComponentType.MetalsSupplied
            ? (data.metalsProductId ?? '')
            : '';
        if (out.type === ObsessedEngineeringComponentType.MetalsSupplied && data.manufacturingCost != null
            && (!Number.isFinite(data.manufacturingCost) || data.manufacturingCost < 0)) {
            throw new Error('Component manufacturing cost must be a non-negative number.');
        }
        out.manufacturingCost = out.type === ObsessedEngineeringComponentType.MetalsSupplied && data.manufacturingCost != null
            ? toCurrencyAmount(data.manufacturingCost)
            : null;
        return out;
    }
    if (collection === Collection.obsessedEngineeringProduct) {
        const out = pickAppwriteFields(data, collection);
        out.qtyOnHand = Number(data.qtyOnHand ?? 0);
        return out;
    }
    if (collection === Collection.obsessedEngineeringProductVersion) {
        const out = pickAppwriteFields(data, collection);
        out.requiresSerial = !!data.requiresSerial;
        out.assemblyCost = toCurrencyAmount(data.assemblyCost);
        out.pickingCost = toCurrencyAmount(data.pickingCost);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        out.releasedOn = data.releasedOn ? new Date(data.releasedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.obsessedEngineeringProductBomLineItem) {
        const out = pickAppwriteFields(data, collection);
        out.quantity = Number(data.quantity ?? 0);
        return out;
    }
    if (collection === Collection.obsessedEngineeringOrder) {
        const out = pickAppwriteFields(data, collection);
        out.releasedOn = data.releasedOn ? new Date(data.releasedOn).toISOString() : null;
        out.files = (data.files ?? []).map((file: any) => typeof file === 'string' ? file : JSON.stringify(file));
        out.changeLog = (data.changeLog ?? []).map((entry: any) => typeof entry === 'string' ? entry : JSON.stringify({
            ...entry,
            createdOn: entry.createdOn ? new Date(entry.createdOn).toISOString() : null,
        }));
        out.dueDate = new Date(data.dueDate).toISOString();
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.obsessedEngineeringOrderedProduct) {
        const out = pickAppwriteFields(data, collection);
        out.qty = Number(data.qty ?? data.quantity ?? 0);
        out.metalsProjectIds = data.metalsProjectIds ?? [];
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.c3Order) {
        const out = pickAppwriteFields(data, collection);
        out.files = (data.files ?? []).map((file: any) => typeof file === 'string' ? file : JSON.stringify(file));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.c3OrderedProduct) {
        const out = pickAppwriteFields(data, collection);
        out.quantity = Number(data.quantity ?? 0);
        out.changeLog = (data.changeLog ?? []).map((entry: any) => typeof entry === 'string' ? entry : JSON.stringify({
            ...entry,
            createdOn: entry.createdOn ? new Date(entry.createdOn).toISOString() : null,
        }));
        out.dueDate = data.dueDate ? new Date(data.dueDate).toISOString() : null;
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.injectionMoldingOrder) {
        const out = pickAppwriteFields(data, collection);
        out.files = (data.files ?? []).map((file: any) => typeof file === 'string' ? file : JSON.stringify(file));
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.injectionMoldingOrderedProduct) {
        const out = pickAppwriteFields(data, collection);
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'quantity')) {
            out.quantity = Number(data.quantity ?? 0);
        }
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'completedQuantity')) {
            out.completedQuantity = Number(data.completedQuantity ?? 0);
        }
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'lotTraceability')) {
            out.lotTraceability = JSON.stringify(fromAppwriteLotTraceability(data.lotTraceability));
        }
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'lotTraceabilityHistory')) {
            out.lotTraceabilityHistory = (data.lotTraceabilityHistory ?? []).map((lotSet: any) => (
                typeof lotSet === 'string' ? lotSet : JSON.stringify({
                    ...lotSet,
                    lotTraceability: fromAppwriteLotTraceability(lotSet.lotTraceability),
                    createdOn: lotSet.createdOn ? new Date(lotSet.createdOn).toISOString() : null,
                })
            ));
        }
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'comments')) {
            out.comments = (data.comments ?? []).map((comment: any) => typeof comment === 'string'
                ? comment
                : JSON.stringify({
                    ...comment,
                    timestamp: comment.timestamp ? new Date(comment.timestamp).toISOString() : null,
                }));
        }
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'changeLog')) {
            out.changeLog = (data.changeLog ?? []).map((entry: any) => typeof entry === 'string' ? entry : JSON.stringify({
                ...entry,
                createdOn: entry.createdOn ? new Date(entry.createdOn).toISOString() : null,
            }));
        }
        for (const key of ['dueDate', 'createdOn', 'updatedOn', 'completedOn']) {
            if (Object.prototype.hasOwnProperty.call(data ?? {}, key)) {
                out[key] = data?.[key] ? new Date(data[key]).toISOString() : null;
            }
        }
        return out;
    }
    if (collection === Collection.metalsLaserProgram) {
        const out = pickAppwriteFields(data, collection);
        out.taskQuantityDict = JSON.stringify(data.taskQuantityDict ?? {});
        out.taskCompletedQuantityDict = JSON.stringify(data.taskCompletedQuantityDict ?? {});
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        out.updatedOn = data.updatedOn ? new Date(data.updatedOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.inventoryTransaction) {
        const out = pickAppwriteFields(data, collection);
        out.createdOn = data.createdOn ? new Date(data.createdOn).toISOString() : null;
        return out;
    }
    if (collection === Collection.clientDevice) {
        const out = pickAppwriteFields(data, collection);
        out.lastCommunicationTimestamp = data.lastCommunicationTimestamp ? new Date(data.lastCommunicationTimestamp).toISOString() : null;
        out.versionTimestamp = toAppwriteDouble(data.versionTimestamp);
        return out;
    }
    if (collection === Collection.clientDeviceLog) {
        const out = pickAppwriteFields(data, collection);
        out.timestamp = data.timestamp ? new Date(data.timestamp).toISOString() : null;
        return out;
    }
    if (collection === Collection.commonProjectUpdate) {
        const out = pickAppwriteFields(data, collection);
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'files')) {
            out.files = toAppwriteJsonArray(data.files);
        }
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'sourceEmail')) {
            out.sourceEmail = data.sourceEmail ? JSON.stringify(data.sourceEmail) : '';
        }
        if (Object.prototype.hasOwnProperty.call(data ?? {}, 'timestamp')) {
            out.timestamp = data.timestamp ? new Date(data.timestamp).toISOString() : null;
        }
        return out;
    }
    if (
        collection === Collection.purchaseRequest
        || collection === Collection.upsShipment
        || collection === Collection.email
        || collection === Collection.factoryMoveRequest
    ) {
        // Presence-aware so partial update() payloads stay partial. pickAppwriteFields already gates
        // on hasOwnProperty; the json/json-array/date loops must too, otherwise a partial write like
        // update({ ordered, completedOn }) — or the UPS tracking jobs' { status, lastTrackedOn } —
        // would emit every other json/array/date field with a default: blanking billing to {},
        // emptying packages, and nulling processedOn/createdOn/updatedOn — gutting
        // the stored document. A full set() still passes every key, so its output is unchanged.
        const out = pickAppwriteFields(data, collection);
        for (const key of APPWRITE_JSON_FIELDS[collection] ?? []) {
            if (Object.prototype.hasOwnProperty.call(data ?? {}, key)) {
                out[key] = JSON.stringify(data?.[key] ?? {});
            }
        }
        for (const key of APPWRITE_JSON_ARRAY_FIELDS[collection] ?? []) {
            if (Object.prototype.hasOwnProperty.call(data ?? {}, key)) {
                out[key] = toAppwriteJsonArray(data?.[key]);
            }
        }
        for (const key of APPWRITE_DATE_FIELDS[collection] ?? []) {
            if (Object.prototype.hasOwnProperty.call(data ?? {}, key)) {
                out[key] = data?.[key] ? new Date(data[key]).toISOString() : null;
            }
        }
        return out;
    }
    if (
        collection === Collection.c3Graphic
        || collection === Collection.c3GraphicVersion
        || collection === Collection.c3GraphicResource
        || collection === Collection.printingProduct
        || collection === Collection.printingProductVersion
        || collection === Collection.printingProductFile
        || collection === Collection.printingProductBomItem
    ) {
        const out = pickAppwriteFields(data, collection);
        for (const key of APPWRITE_DATE_FIELDS[collection] ?? []) {
            if (Object.prototype.hasOwnProperty.call(data ?? {}, key)) {
                out[key] = data?.[key] ? new Date(data[key]).toISOString() : null;
            }
        }
        return out;
    }
    if (collection === Collection.schematicVersion) {
        // Schematic saves/releases can update just metadata or one drawing array. Missing
        // fields must stay absent; serializing them as []/null would erase the stored drawing.
        const out = pickAppwriteFields(data, collection);
        for (const key of APPWRITE_JSON_ARRAY_FIELDS[collection] ?? []) {
            if (Object.prototype.hasOwnProperty.call(data ?? {}, key)) {
                out[key] = toAppwriteJsonArray(data[key]);
            }
        }
        for (const key of APPWRITE_DATE_FIELDS[collection] ?? []) {
            if (Object.prototype.hasOwnProperty.call(data ?? {}, key)) {
                out[key] = data[key] ? new Date(data[key]).toISOString() : null;
            }
        }
        return out;
    }
    if (APPWRITE_COLLECTION_FIELDS[collection]) {
        const out = pickAppwriteFields(data, collection);
        for (const key of APPWRITE_JSON_FIELDS[collection] ?? []) {
            out[key] = JSON.stringify(data?.[key] ?? {});
        }
        for (const key of APPWRITE_JSON_ARRAY_FIELDS[collection] ?? []) {
            out[key] = toAppwriteJsonArray(data?.[key]);
        }
        for (const key of APPWRITE_DATE_FIELDS[collection] ?? []) {
            out[key] = data?.[key] ? new Date(data[key]).toISOString() : null;
        }
        return out;
    }
    return pickAppwriteFields(data, collection);
}

export function fromAppwrite<T>(collection: Collection, doc: any): T {
    if (collection === Collection.plasticsInvoice) {
        const invoice = JSON.parse(doc.payloadJson);
        for (const key of ['issuedOn', 'dueOn', 'createdOn', 'updatedOn', 'releasedOn', 'paidOn', 'voidedOn']) {
            invoice[key] = invoice[key] ? new Date(invoice[key]) : null;
        }
        return { ...invoice, id: doc.$id ?? doc.id, invoiceNumber: doc.invoiceNumber ?? 0 } as T;
    }
    if ([Collection.plasticsMoldCheckup, Collection.plasticsMoldNotice, Collection.plasticsMoldCheckupCommand].includes(collection)) {
        const payload = JSON.parse(doc.payloadJson, moldCheckupJsonReviver);
        return { ...payload, id: doc.$id ?? doc.id } as T;
    }
    if (collection === Collection.globalVariables) {
        const isMoldCheckup = ['plastics-mold-checkup-settings', 'plastics-mold-checkup-station', 'plastics-mold-checkup-daily-summary'].includes(doc?.kind);
        const parsed = doc?.valueJson ? JSON.parse(doc.valueJson, isMoldCheckup ? moldCheckupJsonReviver : globalVariableJsonReviver) : {};
        if (isMoldCheckup) return { ...parsed, id: doc.$id ?? doc.id } as T;
        return parsed as T;
    }
    const base = pickAppwriteFields(doc, collection, true);
    if (collection === Collection.plasticsHardwareItem) {
        return {
            id: base.id,
            partNumber: base.partNumber ?? '',
            description: base.description ?? '',
            imageUrl: base.imageUrl ?? '',
            thumbnailImageUrl: base.thumbnailImageUrl ?? '',
            qtyOnHand: base.qtyOnHand ?? 0,
            units: base.units ?? null,
            safetyDataSheetResourceId: base.safetyDataSheetResourceId ?? '',
            defaultVendorHardwareDataId: base.defaultVendorHardwareDataId ?? '',
            lastSavedOn: base.lastSavedOn ? new Date(base.lastSavedOn) : null,
            lastSavedById: base.lastSavedById ?? '',
            type: base.type ?? null,
            status: base.status ?? 'Active',
            isUnitCounted: base.isUnitCounted ?? false,
            inventoryControlled: base.inventoryControlled ?? false,
            surchargeHistory: fromAppwriteJsonArray(base.surchargeHistory).map((entry: any) => ({
                ...entry,
                effectiveOn: entry?.effectiveOn ? new Date(entry.effectiveOn) : null,
            })),
        } as T;
    }
    if (collection === Collection.user) {
        // abilities are stored as a stringified JSON blob. Parse, then deep-merge
        // against the typed default so missing keys (after model evolution) don't
        // crash component reads.
        const stored = base.abilities ? JSON.parse(base.abilities) : {};
        base.abilities = deepMerge(EMPTY_USER_ABILITIES, stored);
        const oeAbilities = base.abilities?.[PrismModule.Metals]?.[MetalsSubModule.ObsessedEngineering];
        if (oeAbilities) {
            delete oeAbilities.canAccessEmailManager;
            delete oeAbilities.canAccessInvoices;
        }
        const storedInjectionMoldingOrders = stored?.[PrismModule.InjectionMolding]
            ?.[InjectionMoldingSubModule.Orders];
        const mergedInjectionMoldingOrders = base.abilities?.[PrismModule.InjectionMolding]
            ?.[InjectionMoldingSubModule.Orders];
        // Users who already held the old all-purpose CanEdit ability retain their
        // existing status authority until an administrator explicitly changes it.
        if (storedInjectionMoldingOrders?.CanEdit === true && mergedInjectionMoldingOrders) {
            for (const key of [
                'CanReleaseOrderedProducts',
                'CanCompleteOrderedProducts',
                'CanHoldOrCancelOrderedProducts',
            ]) {
                if (!Object.prototype.hasOwnProperty.call(storedInjectionMoldingOrders, key)) {
                    mergedInjectionMoldingOrders[key] = true;
                }
            }
        }
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.injectionMoldingProduct) {
        base.qtyOnHand = Number(base.qtyOnHand ?? 0);
        return base as T;
    }
    if (collection === Collection.injectionMoldingProductVersion) {
        base.defaultBoxQuantity = base.defaultBoxQuantity ?? null;
        base.floorVisibilityDays = base.floorVisibilityDays ?? null;
        base.estimatedUnitsPerDay = base.estimatedUnitsPerDay ?? null;
        base.productionNotes = base.productionNotes ?? '';
        return base as T;
    }
    if (collection === Collection.vendor) {
        // metalsQuoteRequestMaterialTypes is stored as a stringified JSON Record.
        // Parse on read; default to {} if missing. Components reading
        // vendor.metalsQuoteRequestMaterialTypes[X] get undefined for unknown keys
        // which JS treats as falsy — no crash.
        base.metalsQuoteRequestMaterialTypes = base.metalsQuoteRequestMaterialTypes
            ? JSON.parse(base.metalsQuoteRequestMaterialTypes)
            : {};
        base.isInjectionMoldingVendor = base.isInjectionMoldingVendor ?? false;
        base.lastSavedOn = base.lastSavedOn ? new Date(base.lastSavedOn) : null;
        return base as T;
    }
    if (collection === Collection.contact) {
        // String[] fields (emails, associatedCustomerIds, associatedVendorIds)
        // pass through natively — Appwrite stores them as native arrays.
        // phoneNumbers + primaryPhoneNumber are stringified-JSON, parse here.
        base.phoneNumbers = base.phoneNumbers ? JSON.parse(base.phoneNumbers) : [];
        base.primaryPhoneNumber = base.primaryPhoneNumber ? JSON.parse(base.primaryPhoneNumber) : null;
        base.isOneTimeUse = base.isOneTimeUse ?? false;
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        return base as T;
    }
    if (collection === Collection.customerAddress) {
        // Rebuild the nested `address` object from the flat columns.
        return {
            id: base.id,
            customerId: base.customerId ?? '',
            label: base.label ?? '',
            address: {
                line1: base.line1 ?? '',
                line2: base.line2 ?? '',
                city: base.city ?? '',
                region: base.region ?? '',
                postalCode: base.postalCode ?? '',
                country: base.country ?? '',
            },
            isShippingAddress: base.isShippingAddress ?? false,
            isBillingAddress: base.isBillingAddress ?? false,
            isDefault: base.isDefault ?? false,
            isOneTimeUse: base.isOneTimeUse ?? false,
            receiverName: base.receiverName ?? '',
            contactId: base.contactId ?? '',
            createdOn: base.createdOn ? new Date(base.createdOn) : null,
            createdById: base.createdById ?? '',
            updatedOn: base.updatedOn ? new Date(base.updatedOn) : null,
            updatedById: base.updatedById ?? '',
        } as T;
    }
    if (collection === Collection.customer) {
        const legacyPartNumberPrefix = base.partNumberPrefix ?? '';
        return {
            id: base.id,
            name: base.name ?? '',
            terms: base.terms ?? '',
            imageUrl: base.imageUrl ?? '',
            thumbnailImageUrl: base.thumbnailImageUrl ?? '',
            partNumberPrefix: legacyPartNumberPrefix,
            plasticsPartNumberPrefix: base.plasticsPartNumberPrefix
                || (base.isPlasticsCustomer ? legacyPartNumberPrefix : ''),
            metalsPartNumberPrefix: base.metalsPartNumberPrefix
                || (base.isMetalsCustomer ? legacyPartNumberPrefix : ''),
            c3PartNumberPrefix: base.c3PartNumberPrefix
                || (base.isC3Customer ? legacyPartNumberPrefix : ''),
            injectionMoldingPartNumberPrefix: base.injectionMoldingPartNumberPrefix ?? '',
            isMetalsCustomer: base.isMetalsCustomer ?? false,
            isPlasticsCustomer: base.isPlasticsCustomer ?? false,
            isC3Customer: base.isC3Customer ?? false,
            isInjectionMoldingCustomer: base.isInjectionMoldingCustomer ?? false,
            lastSavedOn: base.lastSavedOn ? new Date(base.lastSavedOn) : null,
            lastSavedById: base.lastSavedById ?? '',
            labelImageType: base.labelImageType ?? null,
            // addresses moved to the customer-address collection; upsAccounts is a string[] of
            // account numbers (tolerating the legacy stringified-object shape).
            upsAccounts: fromAppwriteUpsAccounts(base.upsAccounts),
            // Left undefined where the column does not exist yet, so a re-save does not send it.
            files: base.files === undefined ? undefined : fromAppwriteJsonArray(base.files),
            notesCurrentVersionId: base.notesCurrentVersionId === undefined ? undefined : (base.notesCurrentVersionId ?? ''),
        } as T;
    }
    if (collection === Collection.customerNoteVersion) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        return base as T;
    }
    // Foundation: Product, Resource, Tag.
    if (collection === Collection.product) {
        return {
            id: base.id,
            qtyOnHand: base.qtyOnHand ?? 0,
        } as T;
    }
    if (collection === Collection.resource) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        return base as T;
    }
    if (collection === Collection.tag) {
        return base as T;
    }
    // Product-version core: ProductVersion, ProductVersionMoldConfig,
    // PlasticsMold, PlasticsMoldFamily, PlasticsFinishingStation.
    if (collection === Collection.productVersion) {
        base.plasticsProductCostModelId = base.plasticsProductCostModelId ?? '';
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.releasedOn = base.releasedOn ? new Date(base.releasedOn) : null;
        return base as T;
    }
    if (collection === Collection.productVersionMoldConfig) {
        // No date or nested-object fields — pass through.
        return base as T;
    }
    if (collection === Collection.plasticsMold) {
        base.files = fromAppwriteJsonArray(base.files);
        base.placement = base.placementJson ? JSON.parse(base.placementJson) : null;
        delete base.placementJson;
        base.lastCheckupCycleCount = base.lastCheckupCycleCount ?? 0;
        base.checkupOnNextRemoval = base.checkupOnNextRemoval === true;
        base.serialNumber = base.serialNumber ?? null;
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.moldArmCycle) {
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsMoldFamily) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.armAllocations = fromAppwriteJsonArray(base.armAllocations);
        return base as T;
    }
    if (collection === Collection.metalsProductSetup) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.operations = fromAppwriteJsonArray(base.operations);
        return base as T;
    }
    if (collection === Collection.toolRoomMachine) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsFinishingStation) {
        // taskIds is a native string[], passes through unchanged.
        return base as T;
    }
    // BOM/material: BillOfMaterialsLineItem, PlasticsMaterial.
    if (collection === Collection.billOfMaterialsLineItem) {
        // No date or nested-object fields — pass through.
        return base as T;
    }
    if (collection === Collection.plasticsMaterial) {
        // ingredients arrives as string[] (each element a stringified
        // PlasticsMaterialIngredient); parse each back to an object.
        base.ingredients = (base.ingredients ?? []).map((s: string) => JSON.parse(s));
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    // Quality + labels: PlasticsQualityAlert, PlasticsStaticLabel, 3 mappings.
    if (collection === Collection.plasticsQualityAlert) {
        // No date or nested-object fields — pass through.
        return base as T;
    }
    if (collection === Collection.plasticsStaticLabel) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.mappingProductVersionToQualityAlert) {
        // No date or nested-object fields — pass through.
        return base as T;
    }
    if (collection === Collection.mappingProductVersionToDockInspectionQualityAlert) {
        // No date or nested-object fields — pass through.
        return base as T;
    }
    if (collection === Collection.mappingProductVersionToPlasticsStaticLabel) {
        // No date or nested-object fields — pass through.
        return base as T;
    }
    // View/edit-product dependants.
    if (collection === Collection.plasticsMoldingTask) {
        base.moldId = base.moldId ?? '';
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsSkid) {
        base.uploadedFiles = (base.uploadedFiles ?? []).map((s: string) => JSON.parse(s));
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.shippedOn = base.shippedOn ? new Date(base.shippedOn) : null;
        base.orderedProductCompletedOn = base.orderedProductCompletedOn ? new Date(base.orderedProductCompletedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsOrder) {
        base.files = (base.files ?? []).map((s: string) => JSON.parse(s));
        base.shippingAddressId = base.shippingAddressId ?? '';
        base.shippingContactId = base.shippingContactId ?? '';
        base.pricingChecked = base.pricingChecked ?? false;
        base.estimatingConfirmed = base.estimatingConfirmed ?? false;
        base.acknowledged = base.acknowledged ?? false;
        base.truckingNote = base.truckingNote ?? '';
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsOrderedProduct) {
        base.changeLog = (base.changeLog ?? []).map((s: string) => {
            const entry = JSON.parse(s);
            return {
                ...entry,
                createdOn: entry.createdOn ? new Date(entry.createdOn) : null,
            };
        });
        base.additionalCosts = (base.additionalCosts ?? []).map((value: string | object) => {
            const entry: any = typeof value === 'string' ? JSON.parse(value) : value;
            return { ...entry, createdOn: entry.createdOn ? new Date(entry.createdOn) : null };
        });
        base.dueDate = base.dueDate ? new Date(base.dueDate) : null;
        base.pricingChecked = base.pricingChecked ?? false;
        base.estimatingConfirmed = base.estimatingConfirmed ?? false;
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsCalendarEvent) {
        base.eventDate = base.eventDate ? new Date(base.eventDate) : null;
        return base as T;
    }
    if (collection === Collection.dockInspectionInstance) {
        base.inspectionInstanceHistory = (base.inspectionInstanceHistory ?? []).map((s: string) => {
            const item = JSON.parse(s);
            return {
                ...item,
                createdOn: item.createdOn ? new Date(item.createdOn) : null,
            };
        });
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsMaterialMixingTask) {
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsFinishingTask) {
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsKit) {
        base.items = (base.items ?? []).map((item: string) => typeof item === 'string' ? JSON.parse(item) : item);
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.checkedOutOn = base.checkedOutOn ? new Date(base.checkedOutOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsLabelInstance) {
        base.lineItemData = (base.lineItemData ?? []).map((item: string) => typeof item === 'string' ? JSON.parse(item) : item);
        base.addedHardwareData = (base.addedHardwareData ?? []).map((item: string) => typeof item === 'string' ? JSON.parse(item) : item);
        base.linearTaskList = (base.linearTaskList ?? []).map((item: string) => typeof item === 'string' ? JSON.parse(item) : item);
        base.items = (base.items ?? []).map((item: string) => typeof item === 'string' ? JSON.parse(item) : item);
        base.orderedProductId = base.orderedProductId ?? '';
        base.lotTraceability = fromAppwriteLotTraceability(base.lotTraceability);
        base.printDate = base.printDate ? new Date(base.printDate) : null;
        // Shipment-label-only field; rehydrate to a Date when present.
        if (base.shipDate) base.shipDate = new Date(base.shipDate);
        return base as T;
    }
    if (collection === Collection.rotoMachine) {
        base.arms = (base.arms ?? []).map((arm: string) => typeof arm === 'string' ? JSON.parse(arm) : arm);
        return base as T;
    }
    if (collection === Collection.rotoMachineArmTally) {
        base.timestamp = base.timestamp ? new Date(base.timestamp) : null;
        return base as T;
    }
    if (collection === Collection.plasticsScrapInstance) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsScrapReason) {
        return base as T;
    }
    if (collection === Collection.procedure) {
        return base as T;
    }
    if (collection === Collection.masterProcedure) {
        return base as T;
    }
    if (collection === Collection.masterProcedureVersion) {
        base.steps = base.steps ? JSON.parse(base.steps) : [];
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.releasedOn = base.releasedOn ? new Date(base.releasedOn) : null;
        return base as T;
    }
    if (collection === Collection.procedureInstance) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        return base as T;
    }
    if (collection === Collection.procedureInstanceStep) {
        base.statusUpdatedOn = base.statusUpdatedOn ? new Date(base.statusUpdatedOn) : null;
        base.userInputs = base.userInputs ? JSON.parse(base.userInputs) : [];
        return base as T;
    }
    if (collection === Collection.procedureVersion) {
        // steps arrives as a JSON-stringified blob; parse back to array.
        base.steps = base.steps ? JSON.parse(base.steps) : [];
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.releasedOn = base.releasedOn ? new Date(base.releasedOn) : null;
        return base as T;
    }
    if (collection === Collection.plasticsInventoryLocationBin) {
        return base as T;
    }
    if (
        collection === Collection.plasticsHardwareItemShipment ||
        collection === Collection.obsessedEngineeringComponentShipment
    ) {
        base.uploadedFiles = (base.uploadedFiles ?? []).map((file: any) => typeof file === 'string' ? JSON.parse(file) : file);
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.receivedOn = base.receivedOn ? new Date(base.receivedOn) : null;
        if (collection === Collection.plasticsHardwareItemShipment) {
            base.internalNotes = base.internalNotes ?? base.notes ?? '';
            base.externalNotes = base.externalNotes ?? '';
            delete base.notes;
        } else {
            base.reportEmailIds = Array.isArray(base.reportEmailIds)
                ? base.reportEmailIds
                : base.reportEmailId
                    ? [base.reportEmailId]
                    : [];
            delete base.reportEmailId;
        }
        return base as T;
    }
    if (
        collection === Collection.plasticsInventoryArea ||
        collection === Collection.plasticsInventoryLocation ||
        collection === Collection.plasticsKittingArea ||
        collection === Collection.plasticsKittingLocation ||
        collection === Collection.plasticsKittingLocationBin
    ) {
        return base as T;
    }
    if (collection === Collection.moldRepairRequest || collection === Collection.maintenanceTask) {
        base.files = (base.files ?? []).map((s: string) => JSON.parse(s));
        base.eventsHistory = (base.eventsHistory ?? []).map((s: string) => {
            const event = JSON.parse(s);
            return {
                ...event,
                createdOn: event.createdOn ? new Date(event.createdOn) : null,
            };
        });
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.smartJeffProject) {
        base.files = (base.files ?? []).map((file: any) => typeof file === 'string' ? JSON.parse(file) : file);
        base.updates = (base.updates ?? []).map((item: any) => {
            const update = typeof item === 'string' ? JSON.parse(item) : item;
            return {
                ...update,
                files: (update.files ?? []).map((file: any) => typeof file === 'string' ? JSON.parse(file) : file),
                prevProjectStatus: update.prevProjectStatus ?? null,
                currProjectStatus: update.currProjectStatus ?? null,
                createdOn: update.createdOn ? new Date(update.createdOn) : null,
                updatedOn: update.updatedOn ? new Date(update.updatedOn) : null,
                publishedOn: update.publishedOn ? new Date(update.publishedOn) : null,
            };
        });
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        base.completedById = base.completedById ?? '';
        base.followerIds = base.followerIds ?? [];
        base.status = base.status ?? 'Draft';
        base.summary = base.summary ?? '';
        base.issueMarkdown = base.issueMarkdown ?? '';
        return base as T;
    }
    if (collection === Collection.userNotification) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.acknowledgedOn = base.acknowledgedOn ? new Date(base.acknowledgedOn) : null;
        return base as T;
    }
    if (collection === Collection.vacationEvent) {
        // events made before org chart employees could be picked have no employeeId
        base.employeeId = base.employeeId ?? null;
        base.startDate = base.startDate ? new Date(base.startDate) : null;
        base.endDate = base.endDate ? new Date(base.endDate) : null;
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.scheduledProcedure) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.nextExecutionDate = base.nextExecutionDate ? new Date(base.nextExecutionDate) : null;
        return base as T;
    }
    // View/edit-product additional dependants.
    if (collection === Collection.productInstance) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        return base as T;
    }
    if (collection === Collection.complaintLog) {
        // uploadedFiles + comments arrive as string[] (each element a
        // stringified UploadedFile/UserComment); parse each back to object.
        base.status = base.status || 'unresolved';
        base.uploadedFiles = (base.uploadedFiles ?? []).map((s: string) => JSON.parse(s));
        base.comments = (base.comments ?? []).map((s: string) => {
            const comment = JSON.parse(s);
            return {
                ...comment,
                timestamp: comment.timestamp ? new Date(comment.timestamp) : null,
            };
        });
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        return base as T;
    }
    if (collection === Collection.complaintLogProduct) {
        base.quantity = base.quantity ?? null;
        return base as T;
    }
    if (collection === Collection.plasticsFixture) {
        base.lastSavedOn = base.lastSavedOn ? new Date(base.lastSavedOn) : null;
        return base as T;
    }
    if (collection === Collection.vendorHardwareData) {
        base.lastSavedOn = base.lastSavedOn ? new Date(base.lastSavedOn) : null;
        return base as T;
    }
    if (collection === Collection.metalsHardwareItem || collection === Collection.metalsVendorHardwareData) {
        base.lastSavedOn = base.lastSavedOn ? new Date(base.lastSavedOn) : null;
        return base as T;
    }
    if (collection === Collection.metalsProcessStation) {
        base.transparentForNextTaskActivation = base.transparentForNextTaskActivation ?? false;
        return base as T;
    }
    if (
        collection === Collection.metalsLaserMaterial ||
        collection === Collection.metalsPowderCoatingPowder ||
        collection === Collection.metalsPressBrakeBendRadius ||
        collection === Collection.metalsProduct ||
        collection === Collection.metalsProductBillOfMaterialsLineItem
    ) {
        return base as T;
    }
    if (collection === Collection.metalsProductVersion) {
        base.versionNumber = base.versionNumber ?? null;
        base.releasedById = base.releasedById || null;
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.releasedOn = base.releasedOn ? new Date(base.releasedOn) : null;
        base.packUnitQuantity = base.packUnitQuantity ?? null;
        base.packUnitType = base.packUnitType || PackUnitType.Box;
        return base as T;
    }
    if (collection === Collection.metalsProject) {
        base.sourceProductId = base.sourceProductId ?? '';
        base.sourceProductVersionId = base.sourceProductVersionId ?? '';
        base.sourceTemplateProjectId = base.sourceTemplateProjectId ?? '';
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        return base as T;
    }
    if (collection === Collection.metalsTask) {
        base.nextTaskId = base.nextTaskId || null;
        base.files = (base.files ?? []).map((file: string) => typeof file === 'string' ? JSON.parse(file) : file);
        base.hardwareItemIdToQtyPerPartDict = base.hardwareItemIdToQtyPerPartDict
            ? JSON.parse(base.hardwareItemIdToQtyPerPartDict)
            : {};
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.metalsTaskQuantityChangeEvent) {
        base.timestamp = base.timestamp ? new Date(base.timestamp) : null;
        return base as T;
    }
    if (collection === Collection.metalsOrder) {
        base.files = (base.files ?? []).map((file: string) => typeof file === 'string' ? JSON.parse(file) : file);
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.metalsOrderedProduct) {
        base.changeLog = (base.changeLog ?? []).map((entry: string) => {
            const parsed = typeof entry === 'string' ? JSON.parse(entry) : entry;
            return {
                ...parsed,
                createdOn: parsed.createdOn ? new Date(parsed.createdOn) : null,
            };
        });
        base.dueDate = base.dueDate ? new Date(base.dueDate) : null;
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.obsessedEngineeringComponent) {
        base.customerPartNumber = base.customerPartNumber ?? '';
        base.qtyOnHand = Number(base.qtyOnHand ?? 0);
        base.type = base.type ?? ObsessedEngineeringComponentType.CustomerSupplied;
        base.metalsProductId = base.metalsProductId ?? '';
        base.manufacturingCost = base.type === ObsessedEngineeringComponentType.MetalsSupplied && base.manufacturingCost != null
            ? Number(base.manufacturingCost)
            : null;
        return base as T;
    }
    if (collection === Collection.obsessedEngineeringProduct) {
        base.qtyOnHand = Number(base.qtyOnHand ?? 0);
        return base as T;
    }
    if (collection === Collection.obsessedEngineeringProductVersion) {
        base.requiresSerial = !!base.requiresSerial;
        delete base.manufacturingCost;
        base.assemblyCost = toCurrencyAmount(base.assemblyCost);
        base.pickingCost = toCurrencyAmount(base.pickingCost);
        base.versionNumber = base.versionNumber === null || base.versionNumber === undefined
            ? null
            : Number(base.versionNumber);
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        base.releasedOn = base.releasedOn ? new Date(base.releasedOn) : null;
        return base as T;
    }
    if (collection === Collection.obsessedEngineeringProductBomLineItem) {
        base.quantity = Number(base.quantity ?? 0);
        return base as T;
    }
    if (collection === Collection.obsessedEngineeringOrder) {
        base.releasedOn = base.releasedOn ? new Date(base.releasedOn) : null;
        base.files = (base.files ?? []).map((file: string) => typeof file === 'string' ? JSON.parse(file) : file);
        base.changeLog = (base.changeLog ?? []).map((entry: string) => {
            const parsed = typeof entry === 'string' ? JSON.parse(entry) : entry;
            return {
                ...parsed,
                createdOn: parsed.createdOn ? new Date(parsed.createdOn) : null,
            };
        });
        base.status = base.status ?? 'Draft';
        base.dueDate = base.dueDate ? new Date(base.dueDate) : new Date(base.createdOn ?? Date.now());
        base.notes = base.notes ?? '';
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.obsessedEngineeringOrderedProduct) {
        base.qty = Number(base.qty ?? base.quantity ?? 0);
        base.metalsProjectIds = base.metalsProjectIds ?? [];
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.c3Order) {
        base.files = (base.files ?? []).map((file: string) => typeof file === 'string' ? JSON.parse(file) : file);
        base.customerId = base.customerId ?? '';
        base.customerPurchaseOrderNumber = base.customerPurchaseOrderNumber ?? '';
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.c3OrderedProduct) {
        base.quantity = Number(base.quantity ?? 0);
        base.salesOrderNumber = base.salesOrderNumber ?? '';
        base.status = base.status ?? 'Draft';
        base.graphicId = base.graphicId ?? '';
        base.graphicVersionId = base.graphicVersionId ?? '';
        base.note = base.note ?? '';
        base.translatedNote = base.translatedNote ?? '';
        base.changeLog = (base.changeLog ?? []).map((entry: string) => {
            const parsed = typeof entry === 'string' ? JSON.parse(entry) : entry;
            return {
                ...parsed,
                createdOn: parsed.createdOn ? new Date(parsed.createdOn) : null,
            };
        });
        base.dueDate = base.dueDate ? new Date(base.dueDate) : new Date(base.createdOn ?? Date.now());
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.injectionMoldingOrder) {
        base.files = (base.files ?? []).map((file: string) => typeof file === 'string' ? JSON.parse(file) : file);
        base.customerId = base.customerId ?? '';
        base.customerPurchaseOrderNumber = base.customerPurchaseOrderNumber ?? '';
        base.shippingAddressId = base.shippingAddressId ?? '';
        base.shippingContactId = base.shippingContactId ?? '';
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.injectionMoldingOrderedProduct) {
        base.quantity = Number(base.quantity ?? 0);
        base.completedQuantity = Number(base.completedQuantity ?? 0);
        base.salesOrderNumber = base.salesOrderNumber ?? '';
        base.status = base.status ?? 'Draft';
        base.note = base.note ?? '';
        base.lotTraceability = fromAppwriteLotTraceability(base.lotTraceability);
        base.lotTraceabilityHistory = (base.lotTraceabilityHistory ?? []).flatMap((entry: string | Record<string, unknown>) => {
            try {
                const parsed: any = typeof entry === 'string' ? JSON.parse(entry) : entry;
                const effectiveAfterQuantity = Number(parsed?.effectiveAfterQuantity);
                if (!parsed?.id || !Number.isInteger(effectiveAfterQuantity) || effectiveAfterQuantity < 0) return [];
                return [{
                    ...parsed,
                    effectiveAfterQuantity,
                    lotTraceability: fromAppwriteLotTraceability(parsed.lotTraceability),
                    createdOn: parsed.createdOn ? new Date(parsed.createdOn) : new Date(0),
                }];
            } catch (error) {
                console.warn('[appwrite conversion] invalid Injection Molding lot history JSON', error);
                return [];
            }
        });
        base.comments = (base.comments ?? []).map((entry: string | Record<string, unknown>) => {
            const parsed: any = typeof entry === 'string' ? JSON.parse(entry) : entry;
            return {
                ...parsed,
                timestamp: parsed.timestamp ? new Date(parsed.timestamp) : null,
            };
        });
        base.completedById = base.completedById ?? '';
        base.completedOn = base.completedOn ? new Date(base.completedOn) : null;
        base.changeLog = (base.changeLog ?? []).map((entry: string) => {
            const parsed = typeof entry === 'string' ? JSON.parse(entry) : entry;
            return {
                ...parsed,
                createdOn: parsed.createdOn ? new Date(parsed.createdOn) : null,
            };
        });
        base.dueDate = base.dueDate ? new Date(base.dueDate) : new Date(base.createdOn ?? Date.now());
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.metalsLaserProgram) {
        base.taskQuantityDict = base.taskQuantityDict ? JSON.parse(base.taskQuantityDict) : {};
        base.taskCompletedQuantityDict = base.taskCompletedQuantityDict ? JSON.parse(base.taskCompletedQuantityDict) : {};
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        base.updatedOn = base.updatedOn ? new Date(base.updatedOn) : null;
        return base as T;
    }
    if (collection === Collection.inventoryTransaction) {
        base.createdOn = base.createdOn ? new Date(base.createdOn) : null;
        return base as T;
    }
    if (collection === Collection.clientDevice) {
        base.lastCommunicationTimestamp = base.lastCommunicationTimestamp ? new Date(base.lastCommunicationTimestamp) : null;
        base.versionTimestamp = fromAppwriteNumber(base.versionTimestamp);
        return base as T;
    }
    if (collection === Collection.clientDeviceLog) {
        base.timestamp = base.timestamp ? new Date(base.timestamp) : null;
        return base as T;
    }
    if (APPWRITE_COLLECTION_FIELDS[collection]) {
        for (const key of APPWRITE_JSON_FIELDS[collection] ?? []) {
            // Defensive parse: these are always written as JSON by toAppwrite, but a
            // legacy value may be a plain (non-JSON) string — e.g. shipment addresses
            // that predate the structured model. Fall back to {} instead of throwing.
            try {
                base[key] = base[key] ? JSON.parse(base[key]) : {};
            } catch {
                base[key] = {};
            }
        }
        for (const key of APPWRITE_JSON_ARRAY_FIELDS[collection] ?? []) {
            base[key] = fromAppwriteJsonArray(base[key]);
        }
        for (const key of APPWRITE_DATE_FIELDS[collection] ?? []) {
            base[key] = base[key] ? new Date(base[key]) : null;
        }
        if (
            collection === Collection.injectionMoldingMold
            || collection === Collection.injectionMoldingMoldSetup
            || collection === Collection.plasticsMoldHistoryEvent
        ) {
            base.changeLog = (base.changeLog ?? []).map((entry: any) => ({
                ...entry,
                createdOn: entry?.createdOn ? new Date(entry.createdOn) : null,
            }));
        }
        if (collection === Collection.c3Report) {
            try {
                base.payload = base.payloadJson
                    ? JSON.parse(base.payloadJson, globalVariableJsonReviver)
                    : {};
            } catch {
                base.payload = {};
            }
            delete base.payloadJson;
        }
        if (collection === Collection.capitalizationProject) {
            base.description = base.description ?? '';
            base.notes = base.notes ?? '';
            base.completionDate = base.completionDate ?? null;
            base.pictures = base.pictures ?? [];
            base.amount = base.amount ?? 0;
        }
        if (collection === Collection.commonProject) {
            base.summaryMarkdown = base.summaryMarkdown ?? '';
        }
        if (collection === Collection.commonProjectUpdate) {
            base.markdown = base.markdown ?? '';
            base.files = base.files ?? [];
            try {
                base.sourceEmail = typeof base.sourceEmail === 'string' && base.sourceEmail
                    ? JSON.parse(base.sourceEmail)
                    : null;
            } catch {
                base.sourceEmail = null;
            }
            base.sourceMessageId = base.sourceMessageId ?? '';
        }
        if (collection === Collection.factoryMoveRequest) {
            base.linkedOrderLineIds = Array.isArray(base.linkedOrderLineIds)
                ? base.linkedOrderLineIds
                : [...new Set((base.linkedItems ?? [])
                    .filter((item: any) => item?.entityType === 'order-line'
                        && (item?.orderLine?.orderLineId || item?.entityId))
                    .map((item: any) => item.orderLine?.orderLineId || item.entityId))];
            base.followerIds = Array.isArray(base.followerIds) ? base.followerIds : [];
        }
        if (collection === Collection.plasticsManufacturingRatesVersion) {
            base.resinSurchargePerPound = base.resinSurchargePerPound ?? 0;
            /* rows written before the operations were made to match the quote have
               none of these; a rate that read as null would cost the work at zero */
            base.deflashingCostPerMinute = base.deflashingCostPerMinute ?? 1;
            base.precisionTrimmingCostPerMinute = base.precisionTrimmingCostPerMinute ?? 1;
            base.packagingCostPerMinute = base.packagingCostPerMinute ?? 1;
            base.moldingFixedCostPerPart = base.moldingFixedCostPerPart ?? 2;
        }
        if (collection === Collection.triumvirateBarcodeScanningSession) {
            base.scans = (base.scans ?? []).map((scan: any) => ({
                ...scan,
                scannedOn: scan.scannedOn ? new Date(scan.scannedOn) : null,
            }));
        }
        if (collection === Collection.directChat) {
            base.lastMessage = base.lastMessage ? {
                ...base.lastMessage,
                createdOn: base.lastMessage.createdOn ? new Date(base.lastMessage.createdOn) : null,
            } : undefined;
            base.messages = (base.messages ?? []).map((message: any) => ({
                ...message,
                createdOn: message?.createdOn ? new Date(message.createdOn) : null,
            }));
        }
        if (collection === Collection.purchaseRequest) {
            base.changeLog = (base.changeLog ?? []).map((entry: any) => ({
                ...entry,
                createdOn: entry?.createdOn ? new Date(entry.createdOn) : null,
            }));
            base.comments = (base.comments ?? []).map((comment: any) => ({
                ...comment,
                timestamp: comment?.timestamp ? new Date(comment.timestamp) : null,
            }));
        }
        if (collection === Collection.plasticsShipment) {
            base.comments = (base.comments ?? []).map((comment: any) => ({
                ...comment,
                timestamp: comment?.timestamp ? new Date(comment.timestamp) : null,
            }));
            // Line items are id references only. Built field-by-field rather than
            // spread so the denormalized snapshots older documents still carry
            // (partNumber, customerPartNumber, description, quantity) are dropped at
            // the boundary — they're resolved from the product's released version
            // now, and leaving them on the runtime object invites new code to read a
            // stale value. A blank productId means "resolve it from the ordered
            // product" (part numbers are ambiguous — see PlasticsShipmentLineItem).
            base.lineItems = (base.lineItems ?? []).map((lineItem: any) => ({
                orderedProductId: lineItem?.orderedProductId ?? '',
                productId: lineItem?.productId ?? '',
                plasticsOrderId: lineItem?.plasticsOrderId ?? '',
                salesOrderNumber: lineItem?.salesOrderNumber ?? '',
                purchaseOrderNumber: lineItem?.purchaseOrderNumber ?? '',
                note: lineItem?.note ?? '',
            }));
            // Older shipments predate the shipping method — default to truck.
            base.shippingMethod = base.shippingMethod ?? 'truck';
            // Address/contact selection ids — the shipment's ONLY address reference now (the
            // billing/shipping snapshot columns are gone; the docs behind these ids are resolved
            // at read time). Default to '' on records saved before the customer-address collection.
            base.billingAddressId = base.billingAddressId ?? '';
            base.billingContactId = base.billingContactId ?? '';
            base.shippingAddressId = base.shippingAddressId ?? '';
            base.shippingContactId = base.shippingContactId ?? '';
            base.upsShipmentId = base.upsShipmentId ?? '';
            // Ground "customer schedules the pickup" flag — absent on documents saved
            // before it existed; those were all we-schedule (or truck), so default false.
            base.scheduledByCustomer = base.scheduledByCustomer ?? false;
            // Customer-facing packing-slip note — absent on documents predating it.
            base.packingSlipNote = base.packingSlipNote ?? '';
            // Freight billed to the customer + carrier reference — absent on documents predating them.
            base.freightCharge = typeof base.freightCharge === 'number' && Number.isFinite(base.freightCharge) ? base.freightCharge : 0;
            base.freightReference = base.freightReference ?? '';
        }
        if (collection === Collection.upsShipment) {
            // referenceNumbers is a native string array; default when missing.
            base.referenceNumbers = base.referenceNumbers ?? [];
            base.packages = base.packages ?? [];
            base.notifyRecipients = base.notifyRecipients ?? [];
            // Ship-from selection ids — absent on records predating ship-from existing/new tabs.
            base.shipFromCustomerId = base.shipFromCustomerId ?? '';
            base.shipFromAddressId = base.shipFromAddressId ?? '';
            base.shipFromContactId = base.shipFromContactId ?? '';
            // Residential flags. Their own columns now (the party snapshots that used to carry the
            // flag are gone); the snapshot→ids migration copies the legacy value across, so a
            // record that predates the columns defaults to false here.
            base.shipToResidential = base.shipToResidential ?? false;
            base.shipFromResidential = base.shipFromResidential ?? false;
            // Rate breakdowns are JSON objects; normalize so consumers see defined arrays
            // (legacy docs predate them → {} from the defensive parse).
            const normalizeBreakdown = (b: any) => ({
                currency: b?.currency ?? '',
                transportation: b?.transportation ?? '',
                serviceOptions: b?.serviceOptions ?? '',
                itemized: Array.isArray(b?.itemized) ? b.itemized : [],
                taxes: Array.isArray(b?.taxes) ? b.taxes : [],
                total: b?.total ?? '',
                billingWeight: b?.billingWeight ?? '',
            });
            base.publishedBreakdown = normalizeBreakdown(base.publishedBreakdown);
            base.negotiatedBreakdown = normalizeBreakdown(base.negotiatedBreakdown);
        }
        return base as T;
    }
    return base as T;
}

function toCurrencyAmount(value: unknown): number {
    const numericValue = Number(value ?? 0);
    return Number.isFinite(numericValue) ? Math.round((numericValue + Number.EPSILON) * 100) / 100 : 0;
}

export function fromAppwriteNumber(value: any): number {
    if (typeof value === 'number') {
        return Math.trunc(value);
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
    }
    if (value && typeof value === 'object' && typeof value.$numberLong === 'string') {
        const parsed = Number(value.$numberLong);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

export function toAppwriteDouble(value: any): number {
    const parsed = fromAppwriteNumber(value);
    if (!parsed) {
        return 0;
    }
    return Number.isInteger(parsed) ? parsed + 0.001 : parsed;
}

export function globalVariableJsonReviver(_key: string, value: unknown): unknown {
    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)
    ) {
        return new Date(value);
    }
    return value;
}

function moldCheckupJsonReviver(key: string, value: unknown): unknown {
    return ['createdOn', 'updatedOn', 'startedOn', 'completedOn', 'acknowledgedOn', 'clearedOn'].includes(key)
        ? globalVariableJsonReviver(key, value) : value;
}
